/**
 * Audio: tire on surface, suspension, chain, wind, impacts, ambience, and the mix that
 * ties them to speed and load.
 *
 * OWNER: piece:audio
 *
 * Everything here is synthesised at runtime through the Web Audio API. There are no
 * asset files, no samples, no decodeAudioData — the only source material is two noise
 * buffers generated from a seeded PRNG at graph-build time, and a handful of
 * oscillators. That is a constraint of the project (we ship no binary assets) and it
 * shapes every decision below: a surface is not a recording of that surface, it is a
 * *filter shape plus a grain rate*, and the whole job is choosing those so five
 * surfaces separate by ear.
 *
 * ---------------------------------------------------------------------------------
 * The one structural decision: the mix is a plain-JS model, the graph only renders it
 * ---------------------------------------------------------------------------------
 *
 * `mix` below is a flat bag of numbers — one live level per voice — recomputed every
 * frame from the sim's own state. Web Audio is downstream of it: if a context exists
 * and is running, `push()` copies those numbers onto AudioParams; if not, nothing
 * happens and the model keeps running anyway. Three things fall out of that, and all
 * three are why it is built this way:
 *
 *  1. **The capture harness never gestures**, so it never gets an AudioContext, so it
 *     cannot throw, hang, or accumulate a million unconsumed param automations on a
 *     suspended context whose clock is frozen at 0. It costs the harness some
 *     arithmetic and nothing else.
 *  2. **Nobody on this project can hear.** The only honest verification available is
 *     instrumentation: drive the game through scenarios and assert that the numbers
 *     move the way the physics says they should. Those numbers have to exist
 *     independently of whether an audio device does, or the assertions are untestable.
 *  3. A voice's level is then reviewable as a number in `window.__gripAudio.telemetry()`
 *     next to the speed and slip that produced it.
 *
 * ---------------------------------------------------------------------------------
 * Why the AudioContext is built on the first gesture rather than at init
 * ---------------------------------------------------------------------------------
 *
 * The usual pattern is "construct at startup, resume on gesture". Chrome logs
 * `The AudioContext was not allowed to start` as a console *warning* the moment you
 * construct one without user activation, and `tools/capture/capture.mjs` records
 * warnings alongside errors — so the usual pattern would have put a permanent warning
 * in every capture SUMMARY.md this repo produces. Building the whole graph inside the
 * first gesture handler is also the officially recommended shape, so this costs
 * nothing but a lazy branch.
 *
 * ---------------------------------------------------------------------------------
 * How a surface is made distinguishable
 * ---------------------------------------------------------------------------------
 *
 * Each surface is a `SurfaceVoice` recipe, looked up by the `sound` key that
 * `SurfaceProps` in world/terrain.ts already carries (by key, not by enum ordinal, so
 * inserting a surface there cannot silently re-point every sound). Three axes carry
 * the identity, and they are deliberately far apart so the difference survives a
 * laptop speaker:
 *
 *   band centre    loam 330 Hz .. hardpack 620 .. grass 1100 .. loose 1500 .. rock 2400
 *   grain rate     loam 0.15/m .. root 0.55 .. hardpack 0.35 .. rock 1.6 .. loose 5.5
 *   grain pitch    root 165 Hz (a knock) .. loam 700 .. loose 3000 .. rock 4200 (a tick)
 *
 * Loose is the only surface with a continuous stone-rattle; root is the only one whose
 * grain is *below* the band centre, which is what makes a root section read as being
 * hit rather than ridden over. Those separations are checkable without ears:
 * `__gripAudio.surfaceProfile(key, speed, load, slip)` returns them.
 *
 * ---------------------------------------------------------------------------------
 * Ducking
 * ---------------------------------------------------------------------------------
 *
 * BAR names tire-on-surface as the primary speed cue, so the mix is built around
 * protecting it. Ambience and wind are ducked by the tire's own level (a JS sidechain,
 * visible in telemetry as `duckAmb`/`duckWind`); the tire bus is ducked by nothing
 * except a landing, and only for the ~300 ms the landing owns.
 *
 * The master bus is `highpass 38 Hz -> drive -> compressor -> master gain`. The highpass
 * is ahead of the compressor on purpose (a compressor that can see 25 Hz ducks the mix on
 * energy nobody can hear), and the drive is where the operating level is set — see
 * `MASTER_GAIN`. All three of those exist because the first render of this mix was
 * measured and none of them were right.
 *
 * ---------------------------------------------------------------------------------
 * Space: the image, and the room
 * ---------------------------------------------------------------------------------
 *
 * Round 1 of this piece ran every voice dry into mono buses. A reviewer's summary of
 * what that cost: *"the closed-forest frame and the open ridge sound identical apart
 * from level and filter cutoff; the creek gets louder as you approach but has no
 * bearing, roost is not behind you, and the forest has no enclosure."* Both halves of
 * that are addressed here, and they are different problems:
 *
 * **The image** is where a sound is. Every voice — continuous and one-shot — now ends
 * in a `StereoPannerNode`, and its pan is *computed from a position*, never authored.
 * The listener is the rider (not the chase camera): position is the CoM, heading is the
 * bike's own yaw damped at `LISTEN_YAW_RATE` so a whip does not spin the world. A
 * source at listener-local `(right, fore)` gets
 *   - `pan = right / max(MIN_RADIUS_M, |r|) * spread`, and
 *   - a **back-of-head cue** — `BACK_TILT_DB` of high shelf and `BACK_TRIM` of level,
 *     scaled by how far behind it is — because a pan alone cannot tell front from back
 *     and the rear tire is the voice this piece most needs placed.
 * The tire contact patches are placed at `WheelState.cx/cz`, which are the sim's own
 * world contact points, so when the rear steps out the roost pans to *where the tire
 * actually went*. Nothing is hand-animated; a crossed-up bike images itself.
 *
 * **The room** is what the place does to a sound. Three mechanisms, all driven by
 * things measured from the world rather than by a preset:
 *
 *  1. **Early reflections** — four tapped `DelayNode`s whose time base is the measured
 *     mean distance to real stems from `scatterObstacles()`, converted through
 *     `SOUND_MS`. Riding into tight timber shortens them; a clearing lengthens them.
 *  2. **Two convolution tails**, procedurally generated (`makeImpulse`, no asset
 *     files), crossfaded by what is actually around you: a *forest* IR — dense,
 *     dark, RT60 ~0.5 s, a stand of trunks absorbing everything above 2 kHz — and an
 *     *open* IR — sparse discrete slaps building into a long bright tail, which is what
 *     a cirque does. Both are two-channel with independent noise per channel, so the
 *     tail is genuinely wide rather than a mono blob in the middle.
 *  3. **A terrain slapback** — one delay whose time is the round trip to the *steepest
 *     horizon the landform presents*, panned toward that bearing. This is the "shout in
 *     a valley" cue, and it is the reason a gully and a ridge do not sound alike even
 *     with the same trees.
 *
 * The drivers are probed round-robin at `AMB_HZ` (a couple of `terrain.height` calls a
 * tick, and one scatter query), not per frame.
 *
 * Consequence, which is the point: on an exposed summit `forestClosure` and
 * `confinement` both go to ~0, every wet gain collapses, and the mountain sounds dead
 * and windy. In closed timber `wetForest` and `wetER` come up and the early reflections
 * tighten. In a drainage the slapback arrives. Those are three different places, and
 * they are three different numbers in telemetry.
 *
 * ---------------------------------------------------------------------------------
 * How the space is verified by someone who cannot hear it
 * ---------------------------------------------------------------------------------
 *
 * `mix` proves the *drivers* move; it cannot prove the DSP does anything. So the space
 * chain is built against `BaseAudioContext`, which means the identical constructor runs
 * in an `OfflineAudioContext` — and an offline context needs no user gesture and no
 * audio device. `__gripAudio.probeSpace()` renders the real chain for each place in
 * `SPACE_PLACES`, driving it through `wetLevelsFor()` — the same function the live
 * update calls — and measures on the rendered samples: RT60 by Schroeder backward
 * integration, the arrival time of the first reflection, a high/low band-energy ratio
 * for brightness, and the L/R correlation coefficient for width.
 *
 * What it measured at the time of writing, at 44.1 kHz over a 3 s render of a 4 ms
 * broadband burst:
 *
 *     place        RT60 s   first ms   bright   L/R corr   wet RMS    L-R dB
 *     summit        1.73       38.4     3.25      0.011    0.000092    0.02
 *     forest        1.27       11.4     1.37      0.153    0.005621    0.28
 *     gully         1.64       12.2     2.09      0.117    0.002898    1.54
 *     ir-forest     0.49       11.2     1.39      0.069    0.005069    0.40
 *     ir-open       1.73       38.4     3.25      0.011    0.002440    0.02
 *     slap-only     0.01      320.7     2.05      1.000    0.001424   22.08
 *
 * Read: the summit is **35.7 dB quieter** than the forest — it is effectively dry, which
 * is what an exposed ridge is. The two impulse responses differ by **3.5x in RT60 and
 * 2.3x in brightness**, and their first arrivals are 11 ms against 38 ms, which is the
 * enclosure cue. Both are decorrelated across the pair (corr 0.07 and 0.01), so the
 * tail is a room and not a phantom centre. And with the wall panned hard left the
 * isolated slapback returns **22 dB louder in the left channel at exactly 320.7 ms**,
 * which is 2 x 55 m / 343 — the echo arrives from the bearing and at the range the
 * landform probe found.
 *
 * The placement arithmetic is checked the same way through `probePlacement()`: a source
 * 30 m to the rider's left gives pan -0.950 / bearing -90.0 deg / back 0.00; the rear
 * contact patch gives pan 0.000 / bearing 180.0 / back 1.00 / tilt -7.5 dB; the rear
 * stepped out 0.45 m to the right gives pan +0.672 / bearing 135.0 / back 0.71; and
 * rotating the rider 90 degrees moves a fixed world source from 0 to -90 deg.
 *
 * ---------------------------------------------------------------------------------
 * Round 3: a file
 * ---------------------------------------------------------------------------------
 *
 * The above was the whole of round 2, and its reviewer's verdict was that it did not
 * matter how good the numbers got: *"Nothing anyone can hear has ever been produced.
 * SpaceChain was deliberately built against BaseAudioContext so it could render in an
 * OfflineAudioContext — that trick was then used only on the reverb send. AudioGraph is
 * roughly one type signature and a state-gate from the same treatment."*
 *
 * That is exactly what it was, and both are now done:
 *
 *  1. **`AudioGraph` takes a `BaseAudioContext`.** One signature. The class is otherwise
 *     untouched.
 *  2. **The per-frame parameter writes are a function of a recorded record, not of live
 *     locals.** `update()` fills `frame` — every level, frequency, Q, shelf, pan, delay
 *     time and one-shot trigger — whether or not an audio device exists, and
 *     `renderFrame()` is the only thing that touches a node. So the capture harness,
 *     which never gestures, records a complete instruction stream while riding a real
 *     scenario, and `renderMix()` replays it into an offline context through *the same*
 *     `renderFrame()` and returns a 16-bit stereo WAV.
 *
 * `captures/w3-audio-r3/` now holds ten seconds of `descend`, ten seconds of closed
 * timber (a new `timber` scenario, appended), and a five-surface ident clip that rides
 * the identical piece of trail five times with only the surface recipe forced, so
 * hardpack / loam / loose / rock / root can be heard back to back.
 *
 * **Six things were wrong that no amount of telemetry had shown, and all six were found
 * within minutes of the first render.** They are worth listing because they are the
 * argument for the whole exercise:
 *
 *   - The **sub-40 Hz band was the loudest band in the file** — louder than the tire.
 *     None of it is reproducible on a laptop and all of it was driving the compressor.
 *     Fixed with a 38 Hz highpass ahead of the compressor.
 *   - The **impact bus sat 8.8 dB above the tire** over ten seconds of descending, and
 *     the tire was additionally ducked up to 8 dB by the same impacts. On a build that
 *     lands eight times in ten seconds that inverts the entire brief.
 *   - The **wind was rumble, not air**: a lowpass on pink noise whose energy was almost
 *     entirely under 300 Hz, sitting 9 dB below the whole mix and directly on top of the
 *     tire's own band. Now two bandpasses.
 *   - The **mix was 12-15 dB too quiet** (-29 dBFS RMS on a fast descent, -37 in timber).
 *   - The **right half of the canopy bed could be told to play 14x louder than the left**
 *     in still air — an unbounded ratio with a 0.001 floor under its denominator.
 *   - `loose` measures **11.4 dB louder than hardpack**, and it is `bandQ` rather than
 *     grain level that does it. Nobody would have guessed that from the recipe table.
 *
 * Round 2's other finding — that the room's geometry was degenerate, with the early
 * reflections pinned at their 23 ms floor through timber and the slapback pinned at
 * 204 ms everywhere — is fixed and the fix is visible in the recorded clips: the ER base
 * runs 12.6-23.6 ms through the timber clip (the whole of that range was previously
 * clamped away) and 17.1-47.7 ms on descend, and the slapback runs 143-626 ms against a
 * former constant. Swept over the massif, `wallDistM` now takes 14 distinct values from
 * 20 to 145 m where it used to take one.
 *
 * Measured on the rendered files, ten seconds each, per bus, in dB relative to the full
 * mix (this is the mix balance as an acoustic fact rather than as a nominal gain):
 *
 *     clip      peak   RMS dBFS   tire   mech   impact   wind    amb    60 Hz mod
 *     descend   0.95     -22.1    -2.2  -10.1    -5.9   -16.3  -14.2     -40.9 dB
 *     timber    0.37     -25.7    -1.0  -13.0   -12.0   -22.0  -11.7     -41.8 dB
 *
 * The tire is the loudest bus in both, which is what BAR asks for and what round 2 did
 * not have. The last column is the amplitude modulation at the update rate: a mix whose
 * every parameter is rewritten 60 times a second can buzz at 60 Hz, and at -41 dB this
 * one does not.
 *
 * STILL TRUE, AND IT IS THE POINT OF THE FILES: **nobody has listened to a second of
 * this.** Every claim in this header is a number measured off a render. What is new is
 * that the render is now a file somebody else can play.
 */

import { registerSystem, systemTimings, type SystemContext } from '../core/systems';
import { SurfaceProps } from '../world/terrain';
import { Weather } from '../world/weather';
import { scatterObstacles } from '../world/scatter';
import { clamp, clamp01, damp, lerp, mulberry32, smoothstep, wrapPi } from '../core/math';
import type { WheelState } from '../bike/physics';
import type { ControlInput } from '../input/controls';

// ---------------------------------------------------------------------------------
// Constants. Local per the repo's ownership rules — tuning.ts belongs to physics.
// ---------------------------------------------------------------------------------

/**
 * Master output gain, and the drive into the compressor ahead of it.
 *
 * These two are one decision and they were set by measurement, not by ear (nobody here
 * has ears on this). The first render of ten seconds of `descend` came back at **-29
 * dBFS RMS with a 0.38 peak**, and ten seconds of closed timber at **-37 dBFS** — a mix
 * so quiet that anyone playing the file would reach for the volume before they could
 * judge a single voice in it. Twelve to fifteen dB of the available range was simply
 * unused.
 *
 * Raising the master alone would only have clipped the landings, because the peaks were
 * already where they should be. So the drive goes *before* the compressor: quiet
 * passages get all of it, transients get compressed, and the master then sits below unity
 * so nothing downstream can clip. Target is around -22 dBFS RMS on a fast descent, which
 * is where a game mix normally lives.
 */
export const MASTER_GAIN = 0.72;
const COMP_DRIVE = 2.4;

/**
 * Master highpass, Hz. Nothing on a bicycle belongs below this and no consumer speaker
 * reproduces it. See the note in the AudioGraph constructor — measured on the first
 * render of this mix, the sub-40 Hz band was the *loudest band in the file*.
 */
const MASTER_HP_HZ = 38;

/**
 * Bus trims.
 *
 * These are no longer guesses. Round 3 renders each bus on its own over the same ten
 * seconds of riding and reads its RMS against the full mix, so "the tire is the primary
 * speed cue" is a number rather than an intention. What the first render said, over ten
 * seconds of `descend`:
 *
 *     tire -10.5 dB   mech -8.6   impact -1.7   wind -12.4   amb -18.0
 *
 * The impact bus was 8.8 dB *above* the tire — on a build whose suspension lands eight
 * times and bottoms three times in ten seconds, that is a mix where the primary cue is
 * whatever hit the ground last. BAR asks for the opposite, so the tire comes up and the
 * impacts come down until the tire is the loudest continuous thing in the file and a
 * landing is still the loudest *event* in it.
 */
const BUS_TIRE = 2.0;
const BUS_MECH = 0.85;
const BUS_IMPACT = 0.55;
const BUS_WIND = 0.7;
const BUS_AMB = 1.0;
const BUS_AMBFAR = 1.0;

/** Speed at which the rolling voice is at its reference level, m/s (~43 km/h). */
const SPEED_REF = 12;
/** Normal load at which a tire is "fully weighted", N. Static per-wheel is ~440 N. */
const LOAD_REF = 620;
/** Slip usage below which the tire is simply gripping and has no scrub voice at all. */
const SLIP_GATE = 0.34;
/** Suspension velocity that counts as a full-force hit, m/s. */
const SUS_VEL_REF = 1.35;
/** Speed above which wind is audible at all, m/s, and the span it saturates over. */
const WIND_ON = 3.2;
const WIND_SPAN = 21;

/** Vegetation piece's treeline and its stocking band, m. Shared by value, not import. */
const TREELINE_M = 690;
const TREELINE_BAND_M = 190;
/**
 * The catchment band, m: no water above `CREEK_TOP_M`, full catchment at or below
 * `CREEK_FULL_M`. The gully test below is what decides whether there is a channel here
 * to put water in; this is only "how much of it is there".
 *
 * Round 1 used a single value at 520 m with the band running all the way down to 30 m,
 * and measured across a 72-point sweep of this massif that put every audible creek below
 * ~480 m while every riding scenario sits between 550 and 950 m — so `ambCreek` was
 * *exactly zero in all seven scenarios probed*. The terrain here spans 188 to 936 m; real
 * headwater streams run to about two thirds of relief, which is 680 m, and a catchment
 * is effectively full long before the valley floor. So: 680 down to 380, which puts the
 * gradient right across the altitude band the game is actually ridden in and makes
 * descending into water something you hear happen.
 */
const CREEK_TOP_M = 680;
const CREEK_FULL_M = 380;

/**
 * A landing severity below this is the tire finding the ground, not an impact.
 * A bottom-out below BOTTOM_GATE is the damper's last centimetre, not a clank.
 */
const IMPACT_GATE = 0.06;
const BOTTOM_GATE = 0.16;
/**
 * Minimum seconds between an impact/clank and the next one of the same kind.
 *
 * This is not polish, it is the difference between a game and a machine gun. Measured
 * over the probe set, this build's physics reports up to **331 bottom-outs and 40
 * landings in a 22 s run** — the suspension sits on its stop for most of every
 * scenario (the whole-game reviewer's finding 3). Firing a metallic clank on each one
 * is 15 clanks a second. BAR asks for bottom-outs to be "felt and rare", so the voice
 * re-arms on a timer *and* has to see the event fall away before it fires again.
 */
const IMPACT_REFRACTORY = 0.09;
const BOTTOM_REFRACTORY = 0.22;

/** Scheduling lookahead so a one-shot is never scheduled in the past, s. */
const LOOKAHEAD = 0.012;
/** Hard ceiling on one-shot triggers per second. Beyond this, grain is a texture, and
 *  the excess is folded into the rolling voice's brightness instead of more voices. */
const MAX_ONESHOT_HZ = 64;
/** Analyser window. Only used by `__gripAudio.rms()`, which is a verification hook. */
const ANALYSER_FFT = 2048;

// ---------------------------------------------------------------------------------
// Space model: the image (where a sound is) and the room (what the place does to it)
// ---------------------------------------------------------------------------------

/** Speed of sound, m/s. The only thing that turns a measured distance into a delay. */
const SOUND_MS = 343;

/**
 * Forest probe: radius searched for real stems, m, and the stem count at which the
 * stand is *half* closed.
 *
 * These are stems from `scatterObstacles()` — the vegetation piece's own instanced
 * trees — not a guess from altitude. A canopy term derived from altitude alone says a
 * clearing at 400 m is closed forest, and a clearing is exactly where the reverb should
 * open up.
 *
 * It is a half-point rather than a full-scale point because the first version used
 * `clamp01(n / full)` with a full of 14, and measured across `descend` the real stem
 * count inside 26 m runs 9 to 83 with a median of 46 — so closure sat pinned at 1.0 for
 * most of every run and the forest term discriminated nothing. `n/(n+half)` saturates
 * smoothly instead and never clips: 0 stems -> 0, 9 -> 0.31, 20 -> 0.50, 46 -> 0.70,
 * 83 -> 0.81. Every one of those is a different room.
 */
const FOREST_PROBE_M = 26;
const FOREST_HALF_STEMS = 20;

/**
 * Terrain confinement probe: horizon radii, m, how many bearings, and the elevation
 * angle at which a bearing counts as a fully enclosing wall.
 *
 * Bearings are visited round-robin (two per ambience tick), so the whole ring costs
 * `CONF_RADII.length * 2` height samples every 1/AMB_HZ s and refreshes in ~0.7 s. A
 * 0.34 rad (19.5 deg) horizon is a wall you would hear yourself off.
 *
 * **Six radii, and the range is interpolated between them.** Round 2 used three
 * (35/80/140 m) and picked the *nearest* rung whose horizon angle cleared 6 degrees.
 * On a mountain almost every uphill bearing clears 6 degrees at 35 m — a 6-degree
 * horizon at 35 m is a 3.7 m rise — so the test selected the innermost rung essentially
 * everywhere, and round 2's critic measured exactly that: `wallDistM` pinned to 35 m and
 * `slapMs` sat at 204 ms in every scenario they ran. One echo time for a whole mountain
 * is not a landform cue, it is a preset.
 *
 * What replaces it is the *steepest* horizon rather than the first one over a threshold,
 * refined between rungs by a parabolic vertex fit — so the range is a continuous number
 * over 20..145 m (117..846 ms of round trip) and it moves when the landform does. A
 * bearing that is a uniform slope has no protruding surface and its argmax lands far
 * away, which is correct: on an open hillside the thing that answers you is the ridge,
 * not the ground at your feet. In a drainage the near wall wins by a wide margin.
 */
const CONF_RADII = [20, 30, 45, 68, 100, 145];
const CONF_BEARINGS = 8;
const CONF_FULL_RAD = 0.34;
/** Horizon angle below which a bearing has no surface worth echoing off at all. ~3.4
 *  degrees: shallower than that and the "peak" bearing is only the least-downhill one
 *  and its range means nothing. */
const CONF_ECHO_RAD = 0.06;

/** Stereo width for sources bolted to the bike vs. sources out in the world. Bike
 *  sources are inside your own arms' reach; imaging them hard left/right reads as a
 *  headphone effect rather than as a bicycle. */
const SPREAD_BIKE = 0.55;
const SPREAD_WORLD = 0.95;

/** Floor on the radius used in the azimuth, m. Without it, a source that passes within
 *  centimetres of the listener snaps across the head from one frame to the next. */
const MIN_RADIUS_M = 0.36;

/** Back-of-head cue at fully-behind: dB of high shelf, and fraction of level. This is
 *  what distinguishes the rear tire from the front, which a pan alone cannot do. */
const BACK_TILT_DB = 7.5;
const BACK_TRIM = 0.12;

/** Listener heading follows the bike's yaw at this rate, 1/s. Fast enough that a corner
 *  moves the world, slow enough that a 180 whip does not spin it. */
const LISTEN_YAW_RATE = 5.5;

/** Early-reflection tap times as multiples of the measured trunk round-trip, and their
 *  pans. Ratios are mutually irrational-ish so four taps do not comb into a pitch. */
const ER_MUL = [1, 1.63, 2.41, 3.37];
const ER_PAN = [-0.72, 0.58, 0.83, -0.45];
/**
 * Clamp on the tap base, s. 1.2 m of trunk to 20 m of clearing.
 *
 * The floor was 23 ms — 4 m of trunk — and round 2's critic measured it *pinned* there
 * through closed timber, which is the early-reflection cue collapsing to one fixed comb
 * in the one place it is supposed to be doing the most work. `forestAt` already clamps
 * its mean-nearest range at 1.2 m, so the physical floor is 2 x 1.2 / 343 = 7 ms; every
 * millisecond between 7 and 23 was range the probe had already measured and the clamp
 * was throwing away.
 */
const ER_MIN_S = 0.007;
const ER_MAX_S = 0.115;

/** Slapback bounds, s. 17 m of gully wall to 145 m across a drainage. */
const SLAP_MIN_S = 0.10;
const SLAP_MAX_S = 0.85;

/** Wet send per bus. Impacts are the best reverb exciter in the game and the wind past
 *  your own helmet is not in the room at all, so these are far apart on purpose. */
const SEND_TIRE = 0.22;
const SEND_MECH = 0.24;
const SEND_IMPACT = 0.85;
const SEND_WIND = 0.03;
const SEND_AMB = 0.14;
const SEND_AMBFAR = 0.50;

/** Master wet trim. The whole space chain is scaled by this before the compressor. */
const WET_MASTER = 0.9;

/** Creek distance at which the level is halved, m, and the range over which foliage and
 *  air have taken the top off it. Water carries; it also goes dull fast through timber. */
const CREEK_HALF_M = 38;
const CREEK_DULL_M = 170;

// ---------------------------------------------------------------------------------
// Surface voices
// ---------------------------------------------------------------------------------

interface SurfaceVoice {
  /** Rolling-voice loudness at SPEED_REF under full load, linear. */
  level: number;
  /** Rolling band centre at zero speed, Hz, and how fast it rises with speed. */
  bandHz: number;
  bandPerMs: number;
  /** Band Q. Low is broad and gritty; high is a narrow hum. */
  bandQ: number;
  /** High-shelf trim on the rolling voice, dB. Grit vs. mud. */
  brightDb: number;
  /** Discrete grains per metre travelled at full load. */
  grainPerM: number;
  /** Grain resonance, Hz, its Q, its decay in seconds, and its peak level. */
  grainHz: number;
  grainQ: number;
  grainDecay: number;
  grainLevel: number;
  /** Scrub voice loudness relative to the rolling voice, and its band centre. */
  slipLevel: number;
  slipHz: number;
  /** How much this surface breaks away audibly rather than just getting louder:
   *  loose gravel roars, hardpack screeches, snow says almost nothing. */
  slipQ: number;
}

function voice(v: SurfaceVoice): SurfaceVoice {
  return v;
}

/**
 * Recipes keyed by `SurfaceProps[].sound`.
 *
 * The numbers are chosen against each other, not in isolation. Read the table in the
 * file header before changing one: it is the separation between rows that carries the
 * "distinguishable with your eyes shut" requirement, and moving a single row toward
 * its neighbour costs more than it looks like it does.
 */
const VOICES: Record<string, SurfaceVoice> = {
  // Fast, dry, compacted. A tight mid hum with very little grain — the reference
  // surface everything else is heard against.
  hardpack: voice({
    level: 0.55, bandHz: 620, bandPerMs: 22, bandQ: 1.6, brightDb: -3,
    grainPerM: 0.35, grainHz: 2100, grainQ: 6, grainDecay: 0.035, grainLevel: 0.10,
    slipLevel: 0.95, slipHz: 2400, slipQ: 3.2,
  }),
  // Deep, damp, absorbent. Almost all of the energy is below 500 Hz and the top end is
  // gone; this is the surface that sounds like it is *swallowing* the tire.
  loam: voice({
    level: 0.50, bandHz: 330, bandPerMs: 10, bandQ: 0.9, brightDb: -9,
    grainPerM: 0.15, grainHz: 700, grainQ: 3, grainDecay: 0.07, grainLevel: 0.12,
    slipLevel: 0.58, slipHz: 900, slipQ: 1.1,
  }),
  // Gravel/scree. The loudest surface, the broadest band, and 15x hardpack's grain
  // rate — at 10 m/s that is 55 stones a second, which is a continuous rattle.
  // Grain level trimmed from 0.30 to 0.25 in round 3, and rock's from 0.40 to 0.32,
  // after the ident render put both at the top of the tire bus's peak.
  //
  // Worth recording what that render also showed, because it is not what the table above
  // would lead you to expect: rendered over identical riding, loose measures **11.4 dB
  // louder than hardpack**, and almost none of that is the grain. It is `bandQ` — 0.55
  // against hardpack's 1.6 is three times the bandwidth, which is most of it — plus
  // 3.7 dB of `level` and a 6 dB swing in `brightDb`. Trimming grains barely moved the
  // number (0.1109 -> 0.1107 RMS). If a future round wants loose and hardpack closer
  // together, `bandQ` is the lever, not `grainLevel`.
  loose: voice({
    level: 0.84, bandHz: 1500, bandPerMs: 40, bandQ: 0.55, brightDb: 3,
    grainPerM: 5.5, grainHz: 3000, grainQ: 8, grainDecay: 0.028, grainLevel: 0.25,
    slipLevel: 1.0, slipHz: 3200, slipQ: 1.4,
  }),
  // Slab and chunder. Bright, hard, ringing — high grain Q so each strike has a pitch
  // rather than being a click.
  rock: voice({
    level: 0.72, bandHz: 2400, bandPerMs: 55, bandQ: 1.1, brightDb: 6,
    grainPerM: 1.6, grainHz: 4200, grainQ: 12, grainDecay: 0.05, grainLevel: 0.32,
    slipLevel: 0.85, slipHz: 4200, slipQ: 4.5,
  }),
  // Root sections. The base is close to hardpack; the identity is entirely in the
  // grain, which is the only one that sits *below* the rolling band — a 165 Hz knock
  // with a long decay. You hear roots as impacts, not as a texture.
  root: voice({
    level: 0.52, bandHz: 520, bandPerMs: 16, bandQ: 1.4, brightDb: -4,
    grainPerM: 0.55, grainHz: 165, grainQ: 4.5, grainDecay: 0.16, grainLevel: 0.55,
    slipLevel: 0.70, slipHz: 1500, slipQ: 2.0,
  }),
  // Unbuilt hillside. Soft, wide, swishy; the quietest thing you can be rolling on
  // that is not snow.
  grass: voice({
    level: 0.44, bandHz: 1100, bandPerMs: 28, bandQ: 0.7, brightDb: -2,
    grainPerM: 0.5, grainHz: 1800, grainQ: 3, grainDecay: 0.05, grainLevel: 0.10,
    slipLevel: 0.50, slipHz: 1600, slipQ: 1.0,
  }),
  // Alpine snow. Dark and dead — the top three octaves are simply absent, which is
  // what makes it read as snow rather than as quiet dirt.
  snow: voice({
    level: 0.42, bandHz: 260, bandPerMs: 8, bandQ: 0.8, brightDb: -13,
    grainPerM: 0.2, grainHz: 500, grainQ: 2, grainDecay: 0.06, grainLevel: 0.06,
    slipLevel: 0.35, slipHz: 700, slipQ: 0.8,
  }),
};

const FALLBACK_VOICE = VOICES.hardpack;

/**
 * Resolved once: Surface ordinal -> recipe, via the `sound` key SurfaceProps carries.
 * Going through the key rather than the ordinal means a surface inserted in the middle
 * of that array re-points correctly instead of silently making rock sound like loam.
 */
const VOICE_BY_SURFACE: SurfaceVoice[] = SurfaceProps.map(
  (p) => VOICES[p.sound] ?? FALLBACK_VOICE,
);

/** A recipe being crossfaded toward, so a surface change is a slur and not a click. */
interface LiveVoice extends SurfaceVoice { }
function blankVoice(): LiveVoice {
  return { ...FALLBACK_VOICE };
}
/** Crossfade rate between surfaces, 1/s. ~90 ms — fast enough to feel like the ground
 *  changed under you, slow enough that a one-frame misclassification is inaudible. */
const VOICE_BLEND_RATE = 11;

function blendVoice(live: LiveVoice, target: SurfaceVoice, dt: number): void {
  const r = VOICE_BLEND_RATE;
  live.level = damp(live.level, target.level, r, dt);
  live.bandHz = damp(live.bandHz, target.bandHz, r, dt);
  live.bandPerMs = damp(live.bandPerMs, target.bandPerMs, r, dt);
  live.bandQ = damp(live.bandQ, target.bandQ, r, dt);
  live.brightDb = damp(live.brightDb, target.brightDb, r, dt);
  live.grainPerM = damp(live.grainPerM, target.grainPerM, r, dt);
  live.grainHz = damp(live.grainHz, target.grainHz, r, dt);
  live.grainQ = damp(live.grainQ, target.grainQ, r, dt);
  live.grainDecay = damp(live.grainDecay, target.grainDecay, r, dt);
  live.grainLevel = damp(live.grainLevel, target.grainLevel, r, dt);
  live.slipLevel = damp(live.slipLevel, target.slipLevel, r, dt);
  live.slipHz = damp(live.slipHz, target.slipHz, r, dt);
  live.slipQ = damp(live.slipQ, target.slipQ, r, dt);
}

// ---------------------------------------------------------------------------------
// The mix model. One flat, preallocated object; zero allocation per frame.
// ---------------------------------------------------------------------------------

/**
 * Every live voice level, as a linear gain, plus the sim inputs that produced them.
 *
 * This is the review surface. A number here is claimed to be what you would hear; if a
 * capture says `tire` is 0.4 while the bike is parked, that is a bug you can see
 * without a speaker.
 */
export interface AudioMix {
  /** 'none' until the first gesture builds a context; then the context's own state. */
  ctxState: string;
  /** Sample rate of the live context, or 0. */
  sampleRate: number;

  // --- sim inputs, echoed so a level is reviewable next to its cause ---------------
  speedKmh: number;
  altitudeM: number;
  frontLoadN: number;
  rearLoadN: number;
  frontSlip: number;
  rearSlip: number;
  airborne: number;
  wetness: number;
  rainRate: number;
  /** Sound key of the surface under the rear wheel this frame. */
  surface: string;

  // --- tire bus -------------------------------------------------------------------
  /** Rolling voice, front and rear, and their sum. The primary speed cue. */
  tireFront: number;
  tireRear: number;
  tire: number;
  /** Rolling band centre actually in use, Hz — the "what surface is this" number. */
  tireBandHz: number;
  tireBandQ: number;
  /** Grain triggers per second being asked for, and the pitch they are firing at. */
  grainHz: number;
  grainPitchHz: number;
  /** Scrub/tear voice level and its band centre. Rises with slipUsage. */
  slip: number;
  slipBandHz: number;
  /** Locked-wheel skid, separate from progressive scrub. */
  skid: number;
  /** Water film hiss on a wet surface. */
  wetHiss: number;

  // --- mechanical bus -------------------------------------------------------------
  /** Damper hiss: compression stroke and rebound stroke, kept apart. */
  suspComp: number;
  suspReb: number;
  susp: number;
  /** Chain slap over chatter, and the drivetrain whir under power. */
  chain: number;
  drivetrain: number;
  /** Brake squeal near the friction limit. */
  brake: number;

  // --- impacts --------------------------------------------------------------------
  /** Decaying envelope of the last landing/bottom-out, 0..1. Weighted by severity. */
  impact: number;
  /** Severity of the most recent landing consumed, 0..1. */
  lastImpact: number;
  /** Landings and bottom-outs the sim reported, and the ones that actually made a
   *  sound after gating and the refractory. Both are published because the ratio
   *  between them is the honest measure of how much this build's suspension behaviour
   *  is being hidden by the mix. */
  impactRaw: number;
  impactCount: number;
  bottomOutRaw: number;
  bottomOutCount: number;

  // --- wind -----------------------------------------------------------------------
  wind: number;
  windBandHz: number;

  // --- ambience -------------------------------------------------------------------
  /** Wind in the canopy — needs trees, so it dies above the treeline. */
  ambTrees: number;
  /** Bare alpine wind — the inverse. */
  ambAlpine: number;
  ambBirds: number;
  ambCreek: number;
  ambRain: number;
  /** Sum of the continuous ambience beds. Birds are impulsive, not a bed, so they are
   *  reported next to this rather than inside it. */
  amb: number;
  /** 0 above the treeline, 1 in closed forest. Drives birds and canopy wind. */
  canopy: number;

  // --- space: what the place does to a sound --------------------------------------
  /** Real stems from `scatterObstacles()` inside FOREST_PROBE_M, and the closure that
   *  implies (0..1). This is the forest, measured, not inferred from altitude. */
  treeStems: number;
  forestClosure: number;
  /** Mean distance to the nearest stems, m. The early-reflection time base. */
  treeNearM: number;
  /** How enclosed the landform is (0 on an exposed summit, 1 in a bowl), and the wall
   *  that answers a shout: its range in m and its bearing relative to heading in deg. */
  confinement: number;
  wallDistM: number;
  wallBearingDeg: number;
  /** Wet gains actually written to the graph. Together these are the "which place is
   *  this" signature: summit ~0/~0, timber high forest, drainage high slap. */
  wetForest: number;
  wetOpen: number;
  wetER: number;
  wetSlap: number;
  /** Delay times in flight, ms. `erMs` is one trunk round trip; `slapMs` is the
   *  landform's. */
  erMs: number;
  slapMs: number;
  /** Total wet sent, relative to the dry sum. The single "is this a place" number. */
  wetDry: number;

  // --- image: where a sound is ------------------------------------------------------
  /** Listener heading, rad — the bike's yaw, damped. */
  listenYaw: number;
  /** Stereo pan actually written, -1 (left) .. +1 (right). */
  panTireF: number;
  panTireR: number;
  panSlip: number;
  panChain: number;
  panCreek: number;
  panWind: number;
  /** How far behind the listener the rear contact patch is, 0..1. Drives the shelf cut
   *  that makes it read as behind rather than merely off-centre. */
  backTireR: number;
  /** Creek bearing relative to heading, deg (0 ahead, +90 to the right), and range. */
  creekBearingDeg: number;
  creekDistM: number;

  // --- mix ------------------------------------------------------------------------
  duckAmb: number;
  duckWind: number;
  duckMech: number;
  master: number;
  /** One-shot voices currently ringing, and how many were dropped for lack of a slot. */
  voicesActive: number;
  voicesDropped: number;
  /** Update-phase cost of this system, ms. */
  cpuMs: number;
}

const mix: AudioMix = {
  ctxState: 'none', sampleRate: 0,
  speedKmh: 0, altitudeM: 0, frontLoadN: 0, rearLoadN: 0, frontSlip: 0, rearSlip: 0,
  airborne: 0, wetness: 0, rainRate: 0, surface: 'hardpack',
  tireFront: 0, tireRear: 0, tire: 0, tireBandHz: 620, tireBandQ: 1.6,
  grainHz: 0, grainPitchHz: 2100,
  slip: 0, slipBandHz: 2400, skid: 0, wetHiss: 0,
  suspComp: 0, suspReb: 0, susp: 0, chain: 0, drivetrain: 0, brake: 0,
  impact: 0, lastImpact: 0, impactRaw: 0, impactCount: 0, bottomOutRaw: 0, bottomOutCount: 0,
  wind: 0, windBandHz: 300,
  ambTrees: 0, ambAlpine: 0, ambBirds: 0, ambCreek: 0, ambRain: 0, amb: 0, canopy: 0,
  treeStems: 0, forestClosure: 0, treeNearM: 0,
  confinement: 0, wallDistM: 0, wallBearingDeg: 0,
  wetForest: 0, wetOpen: 0, wetER: 0, wetSlap: 0, erMs: 0, slapMs: 0, wetDry: 0,
  listenYaw: 0,
  panTireF: 0, panTireR: 0, panSlip: 0, panChain: 0, panCreek: 0, panWind: 0,
  backTireR: 0, creekBearingDeg: 0, creekDistM: 0,
  duckAmb: 1, duckWind: 1, duckMech: 1, master: MASTER_GAIN,
  voicesActive: 0, voicesDropped: 0, cpuMs: 0,
};

// ---------------------------------------------------------------------------------
// The frame: everything the graph is told, for one update, as a flat list of numbers
// ---------------------------------------------------------------------------------

/**
 * One frame of *graph instructions*, separated from the model that produces them.
 *
 * This is the second half of the same idea `mix` is the first half of, and it is what
 * closes round 2's gap. `mix` is the model — it runs whether or not an audio device
 * exists. `frame` is the render list: the exact level, frequency, Q, shelf, pan, delay
 * time and one-shot trigger every node is about to be given. `update()` fills it
 * unconditionally; `renderFrame()` is the only thing that touches Web Audio nodes.
 *
 * Splitting them buys the one thing this piece has never had:
 *
 *   - **The capture harness can record it.** No gesture, no context, no device — the
 *     numbers are computed anyway, so a scenario run can dump a frame list.
 *   - **An `OfflineAudioContext` can replay it.** `AudioGraph` now takes a
 *     `BaseAudioContext`, so the recorded list can be pushed through *the same*
 *     `renderFrame()` into an offline graph and rendered to a WAV faster than real time.
 *     Nobody has to be at a keyboard, and nothing is a second implementation of the mix.
 *
 * It is a flat key list rather than an interface with fields so that a row is trivially
 * a `number[]` — recording is one `map`, transferring it out of the browser is JSON, and
 * the format is self-describing through `__gripAudio.frameKeys()`.
 */
const FRAME_KEYS = [
  'dt',
  // --- tire bus: the protected voice --------------------------------------------
  'tireFLevel', 'tireFHz', 'tireFQ', 'tireFShelf', 'tireFPan',
  'tireRLevel', 'tireRHz', 'tireRQ', 'tireRShelf', 'tireRPan',
  'slipLevel', 'slipHz', 'slipQ', 'slipShelf', 'slipPan',
  'skidLevel', 'skidHz', 'skidShelf', 'skidPan',
  'wetHissLevel', 'wetHissPan',
  // --- mechanical bus -----------------------------------------------------------
  'compLevel', 'compHz', 'compPan',
  'rebLevel', 'rebHz', 'rebPan',
  'chainLevel', 'chainHz', 'chainPan',
  'driveLevel', 'driveHz',
  'brakeALevel', 'brakeAHz', 'brakeBLevel', 'brakeBHz', 'brakePan',
  // --- wind bus -------------------------------------------------------------------
  'windALevel', 'windAHz', 'windAPan',
  'windBLevel', 'windBHz', 'windBPan',
  // --- ambience -------------------------------------------------------------------
  'treesLLevel', 'treesLHz', 'treesRLevel', 'treesRHz',
  'alpineLLevel', 'alpineLHz', 'alpineRLevel', 'alpineRHz',
  'creekLevel', 'creekHz', 'creekTilt', 'creekPan',
  'burbleLevel', 'burbleHz',
  'rainLLevel', 'rainRLevel',
  // --- the room --------------------------------------------------------------------
  'wetForest', 'wetOpen', 'wetER', 'erBaseS', 'wetSlap', 'slapS', 'slapPan', 'slapLpHz',
  'wetOutGain', 'masterGain',
  // --- one-shot drivers -------------------------------------------------------------
  'grainRateF', 'grainRateR', 'grainLoadF', 'grainLoadR', 'grainWet',
  'grainFHz', 'grainFQ', 'grainFDecay', 'grainFLevel',
  'grainRHz', 'grainRQ', 'grainRDecay', 'grainRLevel',
  'chainRate', 'chatter',
  'firedImpact', 'firedBottom', 'crashed', 'hopped',
  'impactSlapHz', 'impactGritHz', 'impactGritQ',
  'birdGate', 'birdLevel',
  'rainTickRate', 'rainTickLevel',
  // --- echoed sim state, so a dumped row reads next to what caused it ---------------
  'speedKmh', 'surfaceIdx', 'altitudeM', 'rearSlip', 'airborne',
] as const;

type FrameKey = (typeof FRAME_KEYS)[number];
export type AudioFrame = Record<FrameKey, number>;

function blankFrame(): AudioFrame {
  const f = {} as AudioFrame;
  for (const k of FRAME_KEYS) f[k] = 0;
  return f;
}

/** The live frame. One object, refilled every update — zero allocation per frame. */
const frame = blankFrame();

/**
 * Recording. Off by default and costed at exactly one branch when it is.
 *
 * A row is allocated per frame while recording, which breaks the repo's
 * zero-allocation-per-frame rule on purpose and only while a probe has explicitly asked
 * for it: the alternative is a round trip out of the browser per frame, which is an
 * order of magnitude slower and changes the thing being measured.
 */
let recRows: number[][] = [];
let recCap = 0;

function frameToRow(f: AudioFrame): number[] {
  const row = new Array<number>(FRAME_KEYS.length);
  for (let i = 0; i < FRAME_KEYS.length; i++) row[i] = f[FRAME_KEYS[i]];
  return row;
}

function rowToFrame(row: ArrayLike<number>, f: AudioFrame): void {
  for (let i = 0; i < FRAME_KEYS.length; i++) f[FRAME_KEYS[i]] = row[i] ?? 0;
}

/**
 * Per-render-target scheduling state.
 *
 * The grain/chain/rain accumulators and the one-shot RNG cannot be module globals any
 * more: an offline render replays a frame list through the same `renderFrame()` and must
 * not consume the live graph's accumulators or advance its noise sequence. One of these
 * belongs to the live graph and each offline render makes its own, which is also what
 * makes a render deterministic and reproducible from the same rows.
 */
interface RenderState {
  grainAccF: number;
  grainAccR: number;
  chainAcc: number;
  rainAcc: number;
  birdTimer: number;
  dropped: number;
  active: number;
  rnd: () => number;
}

function newRenderState(seed: number): RenderState {
  return {
    grainAccF: 0, grainAccR: 0, chainAcc: 0, rainAcc: 0, birdTimer: 3,
    dropped: 0, active: 0, rnd: mulberry32(seed),
  };
}

/** The live graph's scheduling state. */
const liveRS = newRenderState(0x1a2b3c4d);

// ---------------------------------------------------------------------------------
// The graph. Built once, on the first user gesture. Everything above works without it.
// ---------------------------------------------------------------------------------

/**
 * A continuous filtered-noise voice:
 * `source -> bandpass -> [shelf] -> gain -> pan -> bus`.
 *
 * The panner is not optional. Round 1 had none anywhere, and the result was that every
 * one of these was a point source at dead centre — which is a mono mix with extra
 * steps. A `StereoPannerNode` is two gains; there is no reason for a voice not to have
 * one.
 */
interface ContVoice {
  filt: BiquadFilterNode;
  shelf: BiquadFilterNode | null;
  gain: GainNode;
  pan: StereoPannerNode;
}

/** A pooled one-shot: the shared noise runs forever and the gain gates it. */
interface NoiseSlot {
  filt: BiquadFilterNode;
  gain: GainNode;
  pan: StereoPannerNode;
  freeAt: number;
}

/** A pooled tonal one-shot, for landing thumps and bird notes. */
interface ToneSlot {
  osc: OscillatorNode;
  gain: GainNode;
  pan: StereoPannerNode;
  freeAt: number;
}

// ---------------------------------------------------------------------------------
// Impulse responses, generated. There are no asset files in this project.
// ---------------------------------------------------------------------------------

interface IRParams {
  /** Buffer length, s, and the -60 dB decay time, s. */
  seconds: number;
  rt60: number;
  /**
   * Echo density build-up. A real room starts with countable reflections and becomes
   * diffuse; `buildS` is how long that takes and `density0` is where it starts. A
   * forest is diffuse almost immediately (a thousand trunks); a cirque takes half a
   * second to fill in, which is what makes it read as *large*.
   */
  buildS: number;
  density0: number;
  /** Tail lowpass at t=0 and at the end, Hz. Falling = the tail darkens as it decays,
   *  which every real space does and which is most of what "dark" means here. */
  lpStartHz: number;
  lpEndHz: number;
  /** One-pole rumble trim, Hz. */
  hpHz: number;
  /** Discrete reflections, as [timeS, gain] pairs, added on top of the diffuse field.
   *  This is the difference between a reverb and a canyon. */
  slaps: number[];
  seed: number;
}

/**
 * Two-channel impulse response with independent noise per channel.
 *
 * Independent per channel matters more than anything else in here: a mono IR played
 * through a stereo convolver puts the entire tail in the phantom centre, which sounds
 * exactly like the mono mix this piece is replacing. Decorrelated channels are what
 * make the room wrap around the listener, and it costs one extra noise stream.
 *
 * Normalised to unit energy per channel with `convolver.normalize = false`, so a wet
 * gain in telemetry means the same thing for both IRs and the two are comparable.
 */
function makeImpulse(ac: BaseAudioContext, p: IRParams): AudioBuffer {
  const sr = ac.sampleRate;
  const n = Math.max(64, Math.floor(sr * p.seconds));
  const buf = ac.createBuffer(2, n, sr);
  const decayK = 6.907755 / Math.max(0.02, p.rt60); // ln(1000) = -60 dB
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    const rnd2 = mulberry32(p.seed + ch * 0x9e3779b1);
    let lp = 0;
    let hp = 0;
    let prev = 0;
    for (let i = 0; i < n; i++) {
      const t = i / sr;
      // Echo density ramps in as t^2 — the standard image-source build-up.
      const dens = Math.min(1, p.density0 + (1 - p.density0) * (t / p.buildS) ** 2);
      const fire = rnd2() < dens ? rnd2() * 2 - 1 : 0;
      const env = Math.exp(-decayK * t);
      const x = fire * env;
      // Time-varying one-pole lowpass: the tail darkens as it decays.
      const fc = lerp(p.lpStartHz, p.lpEndHz, Math.min(1, t / p.seconds));
      const k = 1 - Math.exp((-2 * Math.PI * fc) / sr);
      lp += (x - lp) * k;
      // One-pole highpass to keep the tail out of the tire's low mids.
      const a = Math.exp((-2 * Math.PI * p.hpHz) / sr);
      hp = a * (hp + lp - prev);
      prev = lp;
      d[i] = hp;
    }
    // Discrete reflections on top: the canyon, not the room.
    for (let s = 0; s + 1 < p.slaps.length; s += 2) {
      const idx = Math.floor(p.slaps[s] * sr) + Math.floor(rnd2() * 0.004 * sr);
      if (idx < 1 || idx >= n - 3) continue;
      const g = p.slaps[s + 1] * (0.75 + 0.5 * rnd2());
      // Three samples wide so it is a reflection and not a click at Nyquist.
      d[idx] += g;
      d[idx + 1] += g * 0.55 * (rnd2() * 2 - 1);
      d[idx + 2] += g * 0.3 * (rnd2() * 2 - 1);
    }
    let energy = 0;
    for (let i = 0; i < n; i++) energy += d[i] * d[i];
    const norm = energy > 1e-9 ? 1 / Math.sqrt(energy) : 0;
    for (let i = 0; i < n; i++) d[i] *= norm;
  }
  return buf;
}

/**
 * Closed timber. Dense from the first millisecond, short, and dark — a stand of firs
 * scatters the mids off the trunks and the needles eat everything above ~2 kHz inside
 * one bounce. No discrete slaps: in a forest there is no single surface to hear.
 */
const IR_FOREST: IRParams = {
  seconds: 0.95, rt60: 0.52, buildS: 0.012, density0: 0.85,
  lpStartHz: 2600, lpEndHz: 620, hpHz: 150, slaps: [], seed: 0x2f6a5c11,
};

/**
 * Open ground with a landform around it — a cirque, a drainage with rock walls. Sparse
 * at first (you can count the first few returns), building into a long, bright tail
 * because rock reflects the top end that needles absorbed.
 */
const IR_OPEN: IRParams = {
  seconds: 2.3, rt60: 1.65, buildS: 0.55, density0: 0.035,
  lpStartHz: 9000, lpEndHz: 2600, hpHz: 110,
  slaps: [0.086, 0.5, 0.163, 0.34, 0.271, 0.22, 0.412, 0.15],
  seed: 0x7d1e93a7,
};

/**
 * The room, as a send bus.
 *
 * Built against `BaseAudioContext` on purpose: the identical constructor runs inside an
 * `OfflineAudioContext`, which needs no user gesture and no audio device, and that is
 * the only way anyone on this project can produce evidence that the DSP does something
 * rather than that a number moved. See `probeSpace()`.
 */
class SpaceChain {
  readonly input: GainNode;
  readonly out: GainNode;
  readonly hp: BiquadFilterNode;

  readonly erDelay: DelayNode[] = [];
  readonly erLp: BiquadFilterNode[] = [];
  readonly erGain: GainNode[] = [];

  readonly preForest: DelayNode;
  readonly convForest: ConvolverNode;
  readonly gForest: GainNode;

  readonly preOpen: DelayNode;
  readonly convOpen: ConvolverNode;
  readonly gOpen: GainNode;

  readonly slapDelay: DelayNode;
  readonly slapLp: BiquadFilterNode;
  readonly slapGain: GainNode;
  readonly slapPan: StereoPannerNode;

  constructor(ac: BaseAudioContext, dest: AudioNode) {
    this.input = ac.createGain();
    this.input.gain.value = 1;

    // Everything the room makes converges here, gets its low end trimmed so the tail
    // cannot mud up the tire's band, and is then scaled by one master wet trim.
    this.hp = ac.createBiquadFilter();
    this.hp.type = 'highpass';
    this.hp.frequency.value = 145;
    this.hp.Q.value = 0.6;
    this.out = ac.createGain();
    this.out.gain.value = 0;
    this.hp.connect(this.out);
    this.out.connect(dest);

    // --- early reflections: real trunks, at their measured range --------------------
    for (let i = 0; i < ER_MUL.length; i++) {
      const d = ac.createDelay(0.5);
      d.delayTime.value = ER_MIN_S * ER_MUL[i];
      const lp = ac.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 3200;
      lp.Q.value = 0.5;
      const g = ac.createGain();
      g.gain.value = 0;
      const p = ac.createStereoPanner();
      p.pan.value = ER_PAN[i];
      this.input.connect(d);
      d.connect(lp);
      lp.connect(g);
      g.connect(p);
      p.connect(this.hp);
      this.erDelay.push(d);
      this.erLp.push(lp);
      this.erGain.push(g);
    }

    // --- two tails ------------------------------------------------------------------
    // A pre-delay is what separates the source from the room. The forest's is short
    // (the nearest trunk is metres away); the open tail's is long (the wall is not).
    this.preForest = ac.createDelay(0.5);
    this.preForest.delayTime.value = 0.011;
    this.convForest = ac.createConvolver();
    this.convForest.normalize = false;
    this.convForest.buffer = makeImpulse(ac, IR_FOREST);
    this.gForest = ac.createGain();
    this.gForest.gain.value = 0;
    this.input.connect(this.preForest);
    this.preForest.connect(this.convForest);
    this.convForest.connect(this.gForest);
    this.gForest.connect(this.hp);

    this.preOpen = ac.createDelay(0.5);
    this.preOpen.delayTime.value = 0.038;
    this.convOpen = ac.createConvolver();
    this.convOpen.normalize = false;
    this.convOpen.buffer = makeImpulse(ac, IR_OPEN);
    this.gOpen = ac.createGain();
    this.gOpen.gain.value = 0;
    this.input.connect(this.preOpen);
    this.preOpen.connect(this.convOpen);
    this.convOpen.connect(this.gOpen);
    this.gOpen.connect(this.hp);

    // --- the landform's own echo ------------------------------------------------------
    // One tap, at the round trip to the steepest horizon around you, panned toward it.
    // This is the cue that a gully and a ridge are not the same place.
    this.slapDelay = ac.createDelay(1.2);
    this.slapDelay.delayTime.value = SLAP_MIN_S;
    this.slapLp = ac.createBiquadFilter();
    this.slapLp.type = 'lowpass';
    this.slapLp.frequency.value = 2400;
    this.slapLp.Q.value = 0.5;
    this.slapGain = ac.createGain();
    this.slapGain.gain.value = 0;
    this.slapPan = ac.createStereoPanner();
    this.slapPan.pan.value = 0;
    this.input.connect(this.slapDelay);
    this.slapDelay.connect(this.slapLp);
    this.slapLp.connect(this.slapGain);
    this.slapGain.connect(this.slapPan);
    this.slapPan.connect(this.hp);
  }
}

/**
 * The whole mix, as a graph.
 *
 * **Built against `BaseAudioContext`, exactly as `SpaceChain` already was.** That is one
 * type signature, and it is the difference between a piece nobody can hear and a piece
 * that writes a WAV. An `OfflineAudioContext` is a `BaseAudioContext`, needs no user
 * gesture and no audio device, and renders faster than real time — so the identical
 * constructor, the identical procedurally generated noise and impulse responses, and the
 * identical per-frame parameter writes can be replayed over telemetry recorded from a
 * real scenario run and handed to a human as a file. See `renderMix()`.
 *
 * Nothing else about the class changed. The live path still hands it an `AudioContext`.
 */
class AudioGraph {
  readonly ac: BaseAudioContext;
  readonly master: GainNode;
  /** Sub-sonic trim ahead of the compressor. See the constructor. */
  readonly hpf: BiquadFilterNode;
  /** Drive into the compressor — where the mix's operating level is actually set. */
  readonly drive: GainNode;
  readonly comp: DynamicsCompressorNode;
  readonly analyser: AnalyserNode;
  /** Backed by an explicit ArrayBuffer: `new Float32Array(n)` widens to ArrayBufferLike
   *  under lib.dom's newer generic typings and will not pass to getFloatTimeDomainData. */
  readonly analyBuf = new Float32Array(new ArrayBuffer(ANALYSER_FFT * 4));

  readonly busTire: GainNode;
  readonly busMech: GainNode;
  readonly busImpact: GainNode;
  readonly busWind: GainNode;
  readonly busAmb: GainNode;
  /** Ambience that is genuinely *out there* — the creek and the birds — kept on its own
   *  bus because it wants three times the reverb send the beds do. A bird in timber is
   *  most of the way to being all reflection. */
  readonly busAmbFar: GainNode;

  readonly space: SpaceChain;

  // Continuous voices.
  readonly vTireF: ContVoice;
  readonly vTireR: ContVoice;
  readonly vSlip: ContVoice;
  readonly vSkid: ContVoice;
  readonly vWet: ContVoice;
  readonly vComp: ContVoice;
  readonly vReb: ContVoice;
  readonly vChain: ContVoice;
  readonly vDrive: ContVoice;
  readonly vBrake1: ContVoice;
  readonly vBrake2: ContVoice;
  readonly vWindBand: ContVoice;
  readonly vWindRoar: ContVoice;
  /** The ambience beds are stereo *pairs* fed from different noise streams and panned
   *  apart. One filtered noise source panned wide is still one signal in both ears; two
   *  decorrelated ones are a canopy you are standing under. Their gust phases differ,
   *  so the wind crosses you instead of swelling at you. */
  readonly vTrees: ContVoice;
  readonly vTreesR: ContVoice;
  readonly vAlpine: ContVoice;
  readonly vAlpineR: ContVoice;
  readonly vCreek: ContVoice;
  readonly vCreekBurble: ContVoice;
  readonly vRain: ContVoice;
  readonly vRainR: ContVoice;

  readonly grainPool: NoiseSlot[] = [];
  readonly mechPool: NoiseSlot[] = [];
  readonly impactPool: NoiseSlot[] = [];
  readonly ambPool: NoiseSlot[] = [];
  readonly thumpPool: ToneSlot[] = [];
  readonly chirpPool: ToneSlot[] = [];

  private readonly sources: AudioBufferSourceNode[] = [];
  private readonly whiteSrc: AudioBufferSourceNode[] = [];
  private readonly pinkSrc: AudioBufferSourceNode[] = [];
  private srcIdx = 0;
  private whiteIdx = 0;
  private pinkIdx = 0;

  constructor(ac: BaseAudioContext) {
    this.ac = ac;

    // --- master chain -------------------------------------------------------------
    //
    // Sub-sonic trim FIRST, then the compressor.
    //
    // The first thing measured on the first render of this mix was that the band *below
    // 40 Hz was the loudest band in the file* — louder than the tire, by 5 dB over the
    // next octave and 12 dB over the tire's own band. None of it is reproducible on a
    // laptop, a phone or most headphones; all of it was hitting the compressor and
    // pulling everything audible down with it. It comes from three unavoidable places:
    // pink noise through the wind roar's 150 Hz lowpass, the damper's 420 Hz lowpass, and
    // the landing thump's pitch envelope.
    //
    // A 12 dB/octave highpass at 38 Hz ahead of the compressor is the whole fix, and it
    // has to be ahead of it: a compressor that can see 25 Hz ducks the mix on energy
    // nobody can hear.
    this.hpf = ac.createBiquadFilter();
    this.hpf.type = 'highpass';
    this.hpf.frequency.value = MASTER_HP_HZ;
    this.hpf.Q.value = 0.707;

    // A compressor rather than a limiter: a bottom-out landing on a rock section is
    // three loud things at once and the point of the compressor is that the tire is
    // still there underneath it when it happens.
    this.drive = ac.createGain();
    this.drive.gain.value = COMP_DRIVE;

    this.comp = ac.createDynamicsCompressor();
    // Glue, not a clamp: a wide knee and a moderate ratio, so the drive above lifts the
    // quiet two thirds of a run and only landings and bump stops are held back. Slightly
    // softer than round 2's -10/4 because it now has real level going into it.
    this.comp.threshold.value = -12;
    this.comp.knee.value = 16;
    this.comp.ratio.value = 3.5;
    this.comp.attack.value = 0.004;
    this.comp.release.value = 0.22;
    this.hpf.connect(this.drive);
    this.drive.connect(this.comp);

    this.master = ac.createGain();
    this.master.gain.value = MASTER_GAIN;

    this.analyser = ac.createAnalyser();
    this.analyser.fftSize = ANALYSER_FFT;

    this.comp.connect(this.master);
    this.master.connect(this.analyser);
    this.master.connect(ac.destination);

    // The room. Buses feed it in parallel with the dry path, each at its own send.
    this.space = new SpaceChain(ac, this.hpf);

    const bus = (g: number, send: number) => {
      const n = ac.createGain();
      n.gain.value = g;
      n.connect(this.hpf);
      if (send > 0) {
        const s = ac.createGain();
        s.gain.value = send;
        n.connect(s);
        s.connect(this.space.input);
      }
      return n;
    };
    this.busTire = bus(BUS_TIRE, SEND_TIRE);
    this.busMech = bus(BUS_MECH, SEND_MECH);
    this.busImpact = bus(BUS_IMPACT, SEND_IMPACT);
    this.busWind = bus(BUS_WIND, SEND_WIND);
    this.busAmb = bus(BUS_AMB, SEND_AMB);
    this.busAmbFar = bus(BUS_AMBFAR, SEND_AMBFAR);

    // --- shared noise -------------------------------------------------------------
    // Six looping sources over two buffers, started at different offsets. One source
    // fanned out to everything would comb when several grains fire together.
    //
    // They are kept in two colour groups rather than one round-robin list because of
    // the stereo beds: a bed's left and right halves must be *decorrelated but
    // spectrally identical*. Alternating white/pink down a single list gives a pair
    // whose two sides differ by several dB through the same filter, which is not width,
    // it is an off-centre image.
    const white = makeNoiseBuffer(ac, 6, false);
    const pink = makeNoiseBuffer(ac, 6, true);
    const t0 = ac.currentTime;
    for (let i = 0; i < 6; i++) {
      const s = ac.createBufferSource();
      s.buffer = i < 3 ? white : pink;
      s.loop = true;
      s.start(t0, (i * 1.37) % 6);
      this.sources.push(s);
      (i < 3 ? this.whiteSrc : this.pinkSrc).push(s);
    }

    const cont = (
      dest: GainNode, type: BiquadFilterType, hz: number, q: number, shelf: boolean,
      pan0 = 0, pinkNoise = false,
    ): ContVoice => {
      const filt = ac.createBiquadFilter();
      filt.type = type;
      filt.frequency.value = hz;
      filt.Q.value = q;
      const gain = ac.createGain();
      gain.gain.value = 0;
      let sh: BiquadFilterNode | null = null;
      if (shelf) {
        sh = ac.createBiquadFilter();
        sh.type = 'highshelf';
        sh.frequency.value = 2200;
        sh.gain.value = 0;
        filt.connect(sh);
        sh.connect(gain);
      } else {
        filt.connect(gain);
      }
      const pan = ac.createStereoPanner();
      pan.pan.value = pan0;
      gain.connect(pan);
      pan.connect(dest);
      this.nextOf(pinkNoise).connect(filt);
      return { filt, shelf: sh, gain, pan };
    };

    // Every tire voice now carries a shelf, including the two that did not need one for
    // surface colour: the shelf is where the back-of-head cue is written, and the slip
    // and skid voices come off the rear contact patch, which is the thing most in need
    // of reading as *behind* rather than as centred.
    this.vTireF = cont(this.busTire, 'bandpass', 620, 1.6, true);
    this.vTireR = cont(this.busTire, 'bandpass', 620, 1.6, true);
    this.vSlip = cont(this.busTire, 'bandpass', 2400, 3.2, true);
    this.vSkid = cont(this.busTire, 'bandpass', 1500, 1.1, true);
    this.vWet = cont(this.busTire, 'highpass', 2600, 0.7, false);

    this.vComp = cont(this.busMech, 'lowpass', 700, 0.9, false, 0, true);
    this.vReb = cont(this.busMech, 'bandpass', 1400, 1.5, false);
    this.vChain = cont(this.busMech, 'bandpass', 4200, 2.2, false);
    this.vDrive = cont(this.busMech, 'bandpass', 900, 8, false);
    // Two high-Q peaks, slightly detuned: one peak is a test tone, two beating against
    // each other is a brake.
    this.vBrake1 = cont(this.busMech, 'bandpass', 2650, 26, false);
    this.vBrake2 = cont(this.busMech, 'bandpass', 3970, 31, false);

    // Wind is two bandpasses, not a bandpass over a lowpass.
    //
    // The lowpass version was pink noise — already -3 dB/octave — through a 150-220 Hz
    // one-pole, and a solo render of it over ten seconds of `descend` came back as a
    // near-constant wash with essentially all of its energy under 300 Hz, sitting 9 dB
    // below the whole mix. That is the second loudest thing in the game, it is rumble
    // rather than air, and it sits directly on top of the tire's own band (loam 330 Hz,
    // root 520, hardpack 620) — the one voice the mix is supposed to protect.
    //
    // Both voices are now bandpasses with usable Q, so the wind occupies the 150 Hz -
    // 2 kHz range a helmet actually makes and stops competing for the bottom octave.
    this.vWindBand = cont(this.busWind, 'bandpass', 320, 0.8, false, -0.5, true);
    this.vWindRoar = cont(this.busWind, 'bandpass', 180, 0.9, false, 0.5, true);

    this.vTrees = cont(this.busAmb, 'lowpass', 900, 0.6, false, -0.62, true);
    this.vTreesR = cont(this.busAmb, 'lowpass', 900, 0.6, false, 0.62, true);
    this.vAlpine = cont(this.busAmb, 'bandpass', 1700, 0.6, false, -0.78, true);
    this.vAlpineR = cont(this.busAmb, 'bandpass', 1700, 0.6, false, 0.78, true);
    this.vCreek = cont(this.busAmbFar, 'bandpass', 1250, 0.8, false);
    this.vCreekBurble = cont(this.busAmbFar, 'bandpass', 620, 4.5, false);
    this.vRain = cont(this.busAmb, 'highpass', 1300, 0.7, false, -0.55);
    this.vRainR = cont(this.busAmb, 'highpass', 1300, 0.7, false, 0.55);

    // --- one-shot pools -----------------------------------------------------------
    const noisePool = (dest: GainNode, n: number, out: NoiseSlot[]) => {
      for (let i = 0; i < n; i++) {
        const filt = ac.createBiquadFilter();
        filt.type = 'bandpass';
        filt.frequency.value = 1000;
        filt.Q.value = 6;
        const gain = ac.createGain();
        gain.gain.value = 0;
        const pan = ac.createStereoPanner();
        filt.connect(gain);
        gain.connect(pan);
        pan.connect(dest);
        this.nextSource().connect(filt);
        out.push({ filt, gain, pan, freeAt: 0 });
      }
    };
    noisePool(this.busTire, 16, this.grainPool);
    noisePool(this.busMech, 10, this.mechPool);
    noisePool(this.busImpact, 8, this.impactPool);
    // Rain ticks are rain hitting *you*. They belong on the near bed's bus, not on the
    // far one with the creek and the birds.
    noisePool(this.busAmb, 5, this.ambPool);

    const tonePool = (dest: GainNode, n: number, type: OscillatorType, out: ToneSlot[]) => {
      for (let i = 0; i < n; i++) {
        const osc = ac.createOscillator();
        osc.type = type;
        osc.frequency.value = 80;
        const gain = ac.createGain();
        gain.gain.value = 0;
        const pan = ac.createStereoPanner();
        osc.connect(gain);
        gain.connect(pan);
        pan.connect(dest);
        osc.start(t0);
        out.push({ osc, gain, pan, freeAt: 0 });
      }
    };
    tonePool(this.busImpact, 4, 'sine', this.thumpPool);
    tonePool(this.busAmbFar, 3, 'sine', this.chirpPool);
  }

  private nextSource(): AudioBufferSourceNode {
    const s = this.sources[this.srcIdx % this.sources.length];
    this.srcIdx++;
    return s;
  }

  /** Round-robin within one noise colour. Two calls in a row return two different
   *  sources with identical spectra — which is exactly what a stereo bed needs. */
  private nextOf(pinkNoise: boolean): AudioBufferSourceNode {
    if (pinkNoise) return this.pinkSrc[this.pinkIdx++ % this.pinkSrc.length];
    return this.whiteSrc[this.whiteIdx++ % this.whiteSrc.length];
  }

  /** RMS of the master bus over one analyser window. Real evidence that sound exists. */
  rms(): number {
    this.analyser.getFloatTimeDomainData(this.analyBuf);
    let acc = 0;
    for (let i = 0; i < this.analyBuf.length; i++) acc += this.analyBuf[i] * this.analyBuf[i];
    return Math.sqrt(acc / this.analyBuf.length);
  }

  dispose(): void {
    for (const s of this.sources) { try { s.stop(); } catch { /* already stopped */ } }
    for (const t of this.thumpPool) { try { t.osc.stop(); } catch { /* ditto */ } }
    for (const t of this.chirpPool) { try { t.osc.stop(); } catch { /* ditto */ } }
    // `close()` is on AudioContext, not on BaseAudioContext: an offline render has
    // nothing to close and calling it would throw on the one path that has no try/catch
    // around it. Feature-detected rather than type-asserted so a browser that grows
    // `OfflineAudioContext.close()` is still handled.
    const closable = this.ac as { close?: () => Promise<void> };
    if (typeof closable.close === 'function') void closable.close();
  }
}

/**
 * Noise, generated rather than loaded. Pink is Paul Kellet's economy filter — a -3
 * dB/octave tilt, which is what makes a wide band read as "air moving" instead of as
 * a hiss. White stays flat for grains and scrub, where the tilt would eat the top.
 */
function makeNoiseBuffer(ac: BaseAudioContext, seconds: number, pinkFilter: boolean): AudioBuffer {
  const n = Math.floor(ac.sampleRate * seconds);
  const buf = ac.createBuffer(1, n, ac.sampleRate);
  const d = buf.getChannelData(0);
  const rnd = mulberry32(pinkFilter ? 0x9e3779b1 : 0x5bf03635);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < n; i++) {
    const w = rnd() * 2 - 1;
    if (!pinkFilter) { d[i] = w * 0.55; continue; }
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.09;
    b6 = w * 0.115926;
  }
  // A loop point that lands mid-waveform ticks; cross-fade the seam.
  const fade = Math.min(2048, n >> 4);
  for (let i = 0; i < fade; i++) {
    const t = i / fade;
    d[i] = d[i] * t + d[n - fade + i] * (1 - t);
  }
  return buf;
}

// ---------------------------------------------------------------------------------
// Param plumbing. Every write is guarded on a live, running context.
// ---------------------------------------------------------------------------------

let graph: AudioGraph | null = null;
/** The live graph's context, as an `AudioContext` rather than the `BaseAudioContext`
 *  the graph itself now holds. `resume()` and `close()` live only on this one. */
let liveAc: AudioContext | null = null;
/** Held from init purely so the verification hooks can run the world probes at an
 *  arbitrary point rather than only where the bike happens to be. */
let sysCtx: SystemContext | null = null;
/** Set once a gesture has been seen, whether or not the context came up. */
let gestureSeen = false;
let buildFailed = false;
let enabled = true;

/** Smoothing time constants, s. Level changes fast enough to feel connected to the
 *  bike; filter sweeps slower, because a band centre that tracks per-frame noise
 *  warbles. */
const TAU_LEVEL = 0.03;
const TAU_FILTER = 0.055;
/** Pan moves on its own, slower, constant: a pan that tracks per-frame contact-patch
 *  jitter flutters across the head, and the ear is far more sensitive to a moving image
 *  than to a moving cutoff. */
const TAU_PAN = 0.09;
/** Delay times sweep glacially — a delay line swept fast is a pitch shift. The space
 *  they describe changes over seconds anyway. */
const TAU_DELAY = 1.1;

function setParam(p: AudioParam, v: number, now: number, tau: number): void {
  p.setTargetAtTime(v, now, tau);
}

function setVoice(
  v: ContVoice, level: number, hz: number, q: number, shelfDb: number, pan: number,
  now: number,
): void {
  setParam(v.gain.gain, level, now, TAU_LEVEL);
  setParam(v.filt.frequency, clamp(hz, 25, 18000), now, TAU_FILTER);
  setParam(v.filt.Q, clamp(q, 0.05, 40), now, TAU_FILTER);
  if (v.shelf) setParam(v.shelf.gain, clamp(shelfDb, -40, 24), now, TAU_FILTER);
  setParam(v.pan.pan, clamp(pan, -1, 1), now, TAU_PAN);
}

// ---------------------------------------------------------------------------------
// Placement. Where a sound is, resolved to a pan, a shelf tilt and a level trim.
// ---------------------------------------------------------------------------------

/**
 * The listener is the *rider*, not the chase camera.
 *
 * A camera-referenced listener sitting four metres back would put the entire bicycle
 * in front of you, which makes the phrase "roost is behind you" unsatisfiable and makes
 * the image swing every time the camera lags through a corner. The rider's own frame is
 * where the sounds of a bicycle are, and it is the frame a rider's expectations are in.
 * Yaw is damped so a whip rotates the world smoothly rather than snapping it.
 */
let listenYaw = 0;
let listenYawInit = false;

/** Result of a placement. Module-scope and reused — this runs a dozen times a frame and
 *  the repo rule is zero allocation in per-frame paths. */
const place = { pan: 0, back: 0, tiltDb: 0, trim: 1, distM: 0, sinAz: 0, cosAz: 1 };

/**
 * Listener-local offset -> image.
 *
 * `pan` is the sine of the azimuth, which is the correct law for a stereo pair and
 * degrades gracefully as the source approaches the head. `back` is the front/back
 * component, and it exists because a pan cannot express it: two sources at +30 and +150
 * degrees pan identically. A high-shelf cut plus a small level trim is the cheap
 * standing-in for the pinna shadow that tells them apart, and it is the whole reason the
 * rear tire reads as rear.
 */
function placeLocal(right: number, fore: number, spread: number): void {
  const r = Math.max(MIN_RADIUS_M, Math.hypot(right, fore));
  place.distM = r;
  place.sinAz = right / r;
  place.cosAz = fore / r;
  place.pan = clamp(place.sinAz * spread, -1, 1);
  place.back = clamp01(-place.cosAz);
  place.tiltDb = -BACK_TILT_DB * place.back;
  place.trim = 1 - BACK_TRIM * place.back;
}

/** Azimuth of the last placement in degrees: 0 straight ahead, +90 hard right, 180
 *  directly behind. Telemetry only — it is what a reviewer reads to check a bearing. */
function placeBearingDeg(): number {
  return (Math.atan2(place.sinAz, place.cosAz) * 180) / Math.PI;
}

/** World offset from the listener -> image, through the damped listener heading. */
function placeWorld(dx: number, dz: number, spread: number): void {
  const c = Math.cos(listenYaw);
  const s = Math.sin(listenYaw);
  // Body axes match the sim's: forward = (cos yaw, sin yaw), right = (-sin yaw, cos yaw).
  placeLocal(-dx * s + dz * c, dx * c + dz * s, spread);
}

/** Claim the pool slot that will be free soonest. Returns null if all are still loud. */
function claim(pool: NoiseSlot[], now: number): NoiseSlot | null {
  let best: NoiseSlot | null = null;
  for (let i = 0; i < pool.length; i++) {
    const s = pool[i];
    if (s.freeAt <= now) return s;
    if (!best || s.freeAt < best.freeAt) best = s;
  }
  // Steal only if the oldest is close enough to done that cutting it is inaudible.
  return best && best.freeAt - now < 0.05 ? best : null;
}

function claimTone(pool: ToneSlot[], now: number): ToneSlot | null {
  for (let i = 0; i < pool.length; i++) if (pool[i].freeAt <= now) return pool[i];
  return null;
}

/**
 * Gate a slot's gain: silence -> peak in `attack`, then an exponential tail.
 *
 * The pan is set at the same instant with `setValueAtTime` rather than ramped, because
 * a one-shot is where it is when it happens: a grain thrown by the rear tire mid-slide
 * belongs at the rear tire's azimuth for its whole 30 ms, not on a glide toward it.
 */
function pluck(
  slot: NoiseSlot, hz: number, q: number, peak: number, attack: number, decay: number,
  pan: number, now: number,
): void {
  const t = now + LOOKAHEAD;
  slot.filt.frequency.setValueAtTime(clamp(hz, 25, 18000), t);
  slot.filt.Q.setValueAtTime(clamp(q, 0.2, 40), t);
  slot.pan.pan.setValueAtTime(clamp(pan, -1, 1), t);
  const g = slot.gain.gain;
  g.cancelScheduledValues(t);
  g.setValueAtTime(0.0001, t);
  g.linearRampToValueAtTime(Math.max(peak, 0.0002), t + attack);
  g.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.setValueAtTime(0, t + attack + decay + 0.001);
  slot.freeAt = t + attack + decay + 0.002;
}

/** A tonal hit with a downward pitch bend — the body of a landing, or a bird note. */
function thump(
  slot: ToneSlot, f0: number, f1: number, peak: number, attack: number, decay: number,
  pan: number, now: number,
): void {
  const t = now + LOOKAHEAD;
  slot.pan.pan.setValueAtTime(clamp(pan, -1, 1), t);
  const f = slot.osc.frequency;
  f.cancelScheduledValues(t);
  f.setValueAtTime(Math.max(f0, 20), t);
  f.exponentialRampToValueAtTime(Math.max(f1, 18), t + attack + decay);
  const g = slot.gain.gain;
  g.cancelScheduledValues(t);
  g.setValueAtTime(0.0001, t);
  g.linearRampToValueAtTime(Math.max(peak, 0.0002), t + attack);
  g.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.setValueAtTime(0, t + attack + decay + 0.001);
  slot.freeAt = t + attack + decay + 0.002;
}

// ---------------------------------------------------------------------------------
// Sim-side state. Latched at 240 Hz in fixedUpdate, consumed once per rendered frame.
// ---------------------------------------------------------------------------------

const liveFront = blankVoice();
const liveRear = blankVoice();

/** Events are cleared by physics at the top of every step, so a render frame that
 *  spans four steps would miss three of them if we only read in update(). */
let pendLanded = -1;
let pendBottomOut = 0;
let pendCrashed = false;
let pendHopped = false;
let pendScrub = 0;
/** Brake and pedal come from ControlInput, which only fixedUpdate receives. */
let brakeFrontIn = 0;
let brakeRearIn = 0;
let pedalIn = 0;

/** Slow-moving ambience terms, refreshed at AMB_HZ rather than per frame. */
const AMB_HZ = 6;
let ambTimer = 0;
let creekProx = 0;
let gustPhase = 0;
let burblePhase = 0;

/**
 * Space drivers, smoothed toward the probe results.
 *
 * The probes are stepped at AMB_HZ (and the confinement ring one bearing at a time), so
 * their raw output arrives as a staircase. Every one of these is a *room* — the ear
 * notices a room changing far more than it notices a level changing — so they are
 * damped hard and everything downstream reads the damped value.
 */
let forestClosureS = 0;
let treeNearS = FOREST_PROBE_M;
let confinementS = 0;
let wallDistS = 0;
let wallDirXS = 1;
let wallDirZS = 0;
/** The tracked creek, in world coordinates. Smoothed there rather than as a bearing, so
 *  the water stays put on the mountain while the rider moves relative to it. */
let creekWX = 0;
let creekWZ = 0;
let creekHave = false;
let creekDistS = 0;
/**
 * Crossfade for moving the tracked creek to a different channel.
 *
 * Measured, the first version of the world-point tracker snapped when the probe found a
 * new drainage and the image moved a **full stereo width inside one 0.1 s sample** —
 * `panCreek` deltas of 1.16 on `descend`, `roost` and `hucks`. The ear tracks motion far
 * more sharply than position, so a jump like that is more damaging than having no
 * bearing at all. A source that teleports has to be faded out and back in; there is no
 * cheaper honest answer.
 */
let creekFade = 1;
let creekRelocate = false;

/** Smoothed values that need their own time constants. */
let suspCompS = 0;
let suspRebS = 0;
let chatterS = 0;
let chatterSlow = 0;
let impactEnv = 0;
let contactF = 0;
let contactR = 0;
/** Refractory timers and the bump-stop re-arm latch. See IMPACT_REFRACTORY. */
let impactTimer = 0;
let bottomTimer = 0;
let bottomArmed = true;
/** Decaying boost from a scrub/whip impulse; folded into the scrub voice. */
let scrubEnv = 0;

/**
 * Surface override, for the ident render. `null` in normal play.
 *
 * "Loose gravel, hardpack, loam, rock and root must be distinguishable with your eyes
 * shut" is the single requirement in this piece's brief that no number can settle, and
 * until round 3 there was no way to put the five side by side: which surface is under
 * the tire is whatever the heightfield says, and no scenario rides all five. This forces
 * the recipe lookup and nothing else — same speeds, same loads, same slip, same contact
 * — so the ident clip is five surfaces over one piece of riding rather than five
 * different rides.
 */
let surfaceOverride: SurfaceVoice | null = null;
let surfaceOverrideKey = '';

// ---------------------------------------------------------------------------------
// Ambience helpers
// ---------------------------------------------------------------------------------

/** 0 above the treeline, 1 in closed forest. The vegetation piece's own band. */
function canopyAt(alt: number): number {
  return smoothstep(TREELINE_M + TREELINE_BAND_M * 0.45, TREELINE_M - TREELINE_BAND_M, alt);
}

/**
 * Birds are a time-of-day and weather signal, not a constant.
 *
 * Dawn chorus at 5-8, steady through the day, a second smaller peak before dusk, and
 * nothing at night. Rain shuts them up almost entirely.
 */
function birdActivity(hour: number, rain: number): number {
  const dawn = Math.exp(-((hour - 6.3) ** 2) / 3.2);
  const dusk = 0.55 * Math.exp(-((hour - 18.6) ** 2) / 2.4);
  const day = 0.42 * smoothstep(6.5, 9, hour) * (1 - smoothstep(18.5, 20.5, hour));
  return clamp01(dawn + dusk + day) * (1 - 0.92 * clamp01(rain));
}

/**
 * How much this spot sounds like it is in a drainage bottom.
 *
 * Sampled at AMB_HZ, not per frame. A creek is a place, so the cue has to be spatial:
 * three heightfield probes across the local fall line say whether the ground rises on
 * both sides. Altitude alone would put a creek on the summit ridge.
 */
const creekDir = { x: 0, z: 0 };
/**
 * Probe half-widths, m, and the cross-slope rise at each that counts as a full creek.
 *
 * These are not guesses. Surveyed over 72 bearings at seven radii on this heightfield,
 * the min-of-both-banks rise at the 90th percentile position is ~0.4 m at 26 m out,
 * ~1.5 m at 80 m and ~3 m at 140 m — the drainages here are broad, so a narrow probe
 * finds nothing anywhere on the mountain, which is exactly what the first version did.
 * Each radius is normalised by its own scale so all three can vote, and the strongest
 * wins: a tight notch and a wide valley floor are both channels with water in them.
 */
const CREEK_RADII = [40, 90, 170];
const CREEK_RISE_M = [2.5, 4.5, 7.5];
/**
 * How far *above* the channel floor you can still hear the water, in metres of bank.
 *
 * Requiring both banks to be strictly above you makes the creek a switch you stand on:
 * measured, only 2 of 8 bearings around the valley heard anything at all. Water carries
 * a couple of hundred metres, so a position on the shoulder of a drainage gets a
 * fraction of it and the cue becomes something you ride toward.
 */
const CREEK_SHOULDER_M = 2.5;

/**
 * Where the water is, not just how much of it there is.
 *
 * Round 1 returned a scalar proximity, and a reviewer's verdict on that was exactly
 * right: *"the creek gets louder as you approach but has no bearing."* A creek is a
 * line on the ground; if it has no bearing it is not a place, it is a volume knob.
 *
 * So this now returns a bearing and a range as well. The channel floor is found by
 * walking *across* the fall line — the cross-slope axis already computed for the bank
 * test — in whichever direction the ground goes down, until it stops going down. If you
 * are standing in the channel the range collapses toward zero and the source is
 * everywhere; on the shoulder of a drainage it sits out to one side at a real distance,
 * and riding down into it is something you can hear happening.
 */
const creekOut = { prox: 0, wx: 0, wz: 0, dist: 0, found: false };
/** How far across the slope the channel-floor search goes, m, and its step. */
const CREEK_SEEK_M = 240;
const CREEK_SEEK_STEP = 12;

function creekAt(ctx: SystemContext, x: number, z: number, alt: number): void {
  creekOut.prox = 0;
  creekOut.dist = 0;
  creekOut.found = false;
  const near = smoothstep(CREEK_TOP_M, CREEK_FULL_M, alt);
  if (near <= 0.001) return;
  ctx.terrain.descent(x, z, creekDir);
  // Cross-slope axis: perpendicular to steepest descent, in plan.
  const cx = -creekDir.z, cz = creekDir.x;
  const len = Math.hypot(cx, cz) || 1;
  const ux = cx / len, uz = cz / len;
  // Three widths, because the drainages on this massif are broad. A tight 30 m notch
  // and a 150 m valley are both channels with water in the bottom, and testing only
  // the tight one found nothing anywhere on the mountain — the first version of this
  // returned exactly zero at every altitude probed.
  let best = 0;
  for (let i = 0; i < CREEK_RADII.length; i++) {
    const r = CREEK_RADII[i];
    const a = ctx.terrain.height(x + ux * r, z + uz * r);
    const b = ctx.terrain.height(x - ux * r, z - uz * r);
    // Both banks above us = we are in the channel.
    const rise = clamp01((Math.min(a - alt, b - alt) + CREEK_SHOULDER_M) / CREEK_RISE_M[i]);
    if (rise > best) best = rise;
  }
  creekOut.prox = near * best;
  if (creekOut.prox <= 0.001) return;

  // Walk across the slope toward the low side; the channel floor is where it stops
  // dropping. Coarse on purpose: this is a bearing, and it is sampled at AMB_HZ.
  const dirSign = ctx.terrain.height(x + ux * CREEK_SEEK_STEP, z + uz * CREEK_SEEK_STEP)
    <= ctx.terrain.height(x - ux * CREEK_SEEK_STEP, z - uz * CREEK_SEEK_STEP) ? 1 : -1;
  let lowH = alt;
  let lowD = 0;
  for (let d = CREEK_SEEK_STEP; d <= CREEK_SEEK_M; d += CREEK_SEEK_STEP) {
    const h = ctx.terrain.height(x + ux * dirSign * d, z + uz * dirSign * d);
    if (h < lowH) { lowH = h; lowD = d; } else if (d > lowD + CREEK_SEEK_STEP * 2) break;
  }
  // A *point*, not a direction. Tracking the world position and recomputing the bearing
  // from it every frame is what makes the creek sweep from ahead to beside to behind as
  // you ride past it — which is the whole difference between "the water is over there"
  // and "the water is somewhere". A direction alone gets re-derived from a fresh probe
  // six times a second and jitters across the head instead of holding still in the world.
  creekOut.dist = lowD;
  creekOut.wx = x + ux * dirSign * lowD;
  creekOut.wz = z + uz * dirSign * lowD;
  creekOut.found = true;
}

// ---------------------------------------------------------------------------------
// The room's drivers, measured off the world
// ---------------------------------------------------------------------------------

/**
 * How much forest is actually around you.
 *
 * `canopyAt()` above is an altitude band, which is the right driver for *whether birds
 * live here* and the wrong one for *what the sound does*: a clearing at 400 m is inside
 * the band and has no trunks in it, and a clearing is precisely where the reverb should
 * open up. This asks the vegetation piece for its real instanced stems.
 *
 * Cost: one `scatterObstacles` query at AMB_HZ, which walks the live tiles' obstacle
 * arrays. Rate-limited rather than per-frame for that reason; the field it measures
 * changes over metres, not milliseconds.
 */
const stemBuf: number[] = [];
const forestOut = { count: 0, closure: 0, nearM: FOREST_PROBE_M };

function forestAt(x: number, z: number): void {
  const n = scatterObstacles(x, z, FOREST_PROBE_M, stemBuf);
  forestOut.count = n;
  forestOut.closure = n / (n + FOREST_HALF_STEMS);
  if (n <= 0) { forestOut.nearM = FOREST_PROBE_M; return; }
  // Mean of the nearest few, not the nearest one: a single trunk you happen to be
  // beside is not the room, and one close stem would otherwise slam the early
  // reflections to their floor every time you passed a tree.
  let d0 = 1e9, d1 = 1e9, d2 = 1e9;
  for (let k = 0; k + 2 < stemBuf.length; k += 3) {
    const dx = stemBuf[k] - x;
    const dz = stemBuf[k + 1] - z;
    const d = Math.hypot(dx, dz);
    if (d < d0) { d2 = d1; d1 = d0; d0 = d; }
    else if (d < d1) { d2 = d1; d1 = d; }
    else if (d < d2) { d2 = d; }
  }
  let acc = d0;
  let cnt = 1;
  if (d1 < 1e8) { acc += d1; cnt++; }
  if (d2 < 1e8) { acc += d2; cnt++; }
  forestOut.nearM = clamp(acc / cnt, 1.2, FOREST_PROBE_M);
}

/**
 * How enclosed the landform is, and which way the wall that answers a shout is.
 *
 * One bearing per call, round-robin, so the whole ring costs two `terrain.height`
 * samples per ambience tick and refreshes in about 1.3 s. Each bearing keeps the
 * steepest horizon angle it can see at either probe radius; `confinement` is the mean
 * over the ring — 0 on an exposed summit where every bearing falls away, high in a bowl
 * — and the strongest single bearing is where the slapback comes from.
 */
const confAngle = new Float32Array(CONF_BEARINGS);
const confRange = new Float32Array(CONF_BEARINGS);
let confIdx = 0;
const confOut = { confinement: 0, wallDist: 0, wallDirX: 1, wallDirZ: 0 };

/**
 * Measured world -> wet gains and delay times. The whole room, as arithmetic.
 *
 * Factored out of `update()` because `probeSpace()` has to drive the offline render
 * with *these* numbers and not with a hand-copied approximation of them. A probe that
 * measures a second implementation of the model proves nothing about the first, and
 * this file's only claim to being verified rests on those two being the same code.
 *
 * The shape of it:
 *  - forest tail scales with measured stem closure, and a little with enclosure — a
 *    stand of trees in a bowl is a longer room than the same stand on a shoulder.
 *  - open tail is what is left when the trunks have *not* eaten it: a cirque above the
 *    treeline is the biggest reverb on the mountain, and the same cirque full of firs
 *    is not.
 *  - early reflections are trunks, so they die with the trees and tighten as they close.
 *  - the slapback needs a wall, needs enclosure, and needs the trees out of the way.
 */
const wetOut = { forest: 0, open: 0, er: 0, slap: 0, erBaseS: ER_MIN_S, slapS: SLAP_MIN_S };

function wetLevelsFor(
  closure: number, confinement: number, treeNearM: number, wallDistM: number, rain: number,
): void {
  // A wet forest is a louder room, and rain on leaves is itself a diffuse field.
  const roomOn = 1 + 0.25 * clamp01(rain);
  wetOut.forest = closure * (0.58 + 0.4 * confinement) * roomOn;
  wetOut.open = (1 - 0.95 * closure) * Math.pow(clamp01(confinement), 1.3) * 0.5 * roomOn;
  wetOut.er = closure * 0.7 * (1 - 0.35 * clamp01(treeNearM / FOREST_PROBE_M));
  wetOut.slap = wallDistM > 1
    ? clamp01(confinement * 1.6) * (1 - 0.85 * closure) * 0.34
    : 0;
  wetOut.erBaseS = clamp((2 * treeNearM) / SOUND_MS, ER_MIN_S, ER_MAX_S);
  wetOut.slapS = clamp((2 * wallDistM) / SOUND_MS, SLAP_MIN_S, SLAP_MAX_S);
}

/** Horizon angle per probe radius for the bearing being stepped. Module scope so the
 *  refinement below costs no allocation. */
const confProfile = new Float32Array(CONF_RADII.length);

function confinementStep(ctx: SystemContext, x: number, z: number, alt: number): void {
  const i = confIdx % CONF_BEARINGS;
  confIdx++;
  const a = (i / CONF_BEARINGS) * Math.PI * 2;
  const ux = Math.cos(a);
  const uz = Math.sin(a);
  // The whole radial profile of horizon angle, kept: both the enclosure this bearing
  // contributes and the range the echo comes back from are read off it, and reading a
  // range off a profile is what makes it a continuous number instead of a rung.
  let bestAng = 0;
  let bestK = CONF_RADII.length - 1;
  for (let k = 0; k < CONF_RADII.length; k++) {
    const r = CONF_RADII[k];
    const h = ctx.terrain.height(x + ux * r, z + uz * r);
    const ang = Math.atan2(h - alt, r);
    confProfile[k] = ang;
    if (ang > bestAng) { bestAng = ang; bestK = k; }
  }
  confAngle[i] = bestAng;
  // Refine the argmax between rungs with a parabolic vertex fit in log-radius. The
  // sample spacing is geometric, so log-radius is the axis the profile is smooth in, and
  // a vertex fit turns six discrete probes into a range that varies continuously with
  // the landform. Only applied where the profile is genuinely concave at the peak — on a
  // uniform slope there is no protruding surface to refine toward and the rung stands.
  let range = CONF_RADII[bestK];
  if (bestK > 0 && bestK < CONF_RADII.length - 1) {
    const y0 = confProfile[bestK - 1];
    const y1 = confProfile[bestK];
    const y2 = confProfile[bestK + 1];
    const den = y0 - 2 * y1 + y2;
    if (den < -1e-7) {
      const d = clamp((0.5 * (y0 - y2)) / den, -0.98, 0.98);
      const lr = Math.log(CONF_RADII[bestK]);
      const lo = Math.log(CONF_RADII[bestK + (d > 0 ? 1 : -1)]);
      range = Math.exp(lr + (lo - lr) * Math.abs(d));
    }
  }
  confRange[i] = range;

  let sum = 0;
  let peak = -1;
  let peakI = 0;
  for (let k = 0; k < CONF_BEARINGS; k++) {
    sum += clamp01(confAngle[k] / CONF_FULL_RAD);
    if (confAngle[k] > peak) { peak = confAngle[k]; peakI = k; }
  }
  confOut.confinement = sum / CONF_BEARINGS;
  // A wall you cannot hear yourself off is not a wall. Below a few degrees the "peak"
  // bearing is just the least-downhill direction and its range means nothing.
  if (peak < CONF_ECHO_RAD) { confOut.wallDist = 0; return; }
  confOut.wallDist = confRange[peakI];
  const pa = (peakI / CONF_BEARINGS) * Math.PI * 2;
  confOut.wallDirX = Math.cos(pa);
  confOut.wallDirZ = Math.sin(pa);
}

// ---------------------------------------------------------------------------------
// The system
// ---------------------------------------------------------------------------------

function tryBuild(): void {
  if (graph || buildFailed || !enabled) return;
  try {
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) { buildFailed = true; return; }
    const ac = new Ctor({ latencyHint: 'interactive' });
    graph = new AudioGraph(ac);
    // Kept separately from `graph.ac`: that is a `BaseAudioContext` now (so the same
    // class can render offline) and `resume()` is only on the online one.
    liveAc = ac;
    void ac.resume();
  } catch {
    // A device with no audio output, a locked-down context, a browser that refuses:
    // none of them are worth a console error, and the game must still run.
    buildFailed = true;
    graph = null;
  }
}

function onGesture(): void {
  gestureSeen = true;
  tryBuild();
  if (graph && liveAc) {
    if (liveAc.state === 'suspended') void liveAc.resume();
    mix.ctxState = liveAc.state;
    mix.sampleRate = liveAc.sampleRate;
  }
}

registerSystem({
  id: 'audio',
  order: 105,

  init(ctx: SystemContext) {
    sysCtx = ctx;
    if (typeof window === 'undefined') return;

    // Autoplay policy: the context cannot exist before a gesture without Chrome
    // logging a warning that the capture harness would collect. So the graph is built
    // by the first of these, once, and every one of them removes itself.
    const opts = { passive: true } as AddEventListenerOptions;
    const once = (target: EventTarget, ev: string) => {
      const h = () => { target.removeEventListener(ev, h); onGesture(); };
      target.addEventListener(ev, h, opts);
    };
    for (const ev of ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'wheel']) {
      once(window, ev);
    }
    window.addEventListener('gamepadconnected', onGesture, opts);

    (window as unknown as Record<string, unknown>).__gripAudio = {
      /** The live mix. Same object every call — read it, do not keep it. */
      mix,
      telemetry: () => mix,
      state: () => (graph ? graph.ac.state : gestureSeen && buildFailed ? 'failed' : 'none'),
      /** Master-bus RMS over one analyser window, or -1 with no graph. Evidence that
       *  the synthesis is actually producing signal, not just moving numbers. */
      rms: () => (graph ? graph.rms() : -1),
      /** Force the graph up without a gesture — for probes. Chrome may still refuse to
       *  leave 'suspended', which is exactly what `state()` will then say. */
      start: () => { onGesture(); return graph ? graph.ac.state : 'none'; },
      setEnabled: (v: boolean) => {
        enabled = !!v;
        if (graph) graph.master.gain.value = enabled ? MASTER_GAIN : 0;
      },
      /** Steady-state voice numbers for a surface at a given speed/load/slip, computed
       *  by the same code the live mix uses. This is how "loose, hardpack, loam, rock
       *  and root are distinguishable" is checked by someone who cannot hear. */
      surfaceProfile,
      /**
       * Render the real space chain in an OfflineAudioContext and measure the result.
       *
       * This is the piece's answer to "you cannot hear your own work": an offline
       * context needs no gesture and no output device, so it runs inside the capture
       * harness's browser, and what comes back is RT60, first-reflection time,
       * brightness, L/R correlation and L-R balance measured off rendered samples of
       * the same `SpaceChain` the game plays through.
       */
      probeSpace: probeAllSpaces,
      /** Placement arithmetic for an arbitrary world offset — the bearing check. */
      probePlacement,
      /**
       * The world probes evaluated at an arbitrary point, not just where the bike is.
       *
       * Round 1 shipped a creek that measured exactly zero in every scenario, and the
       * reason nobody caught it is that there was no way to ask the question except by
       * riding somewhere and looking at one number. This answers it over a grid.
       */
      probeWorld: (x: number, z: number) => {
        if (!sysCtx) return null;
        const alt = sysCtx.terrain.height(x, z);
        creekAt(sysCtx, x, z, alt);
        forestAt(x, z);
        // The confinement ring is stateful and round-robin; drive a whole ring here so
        // the answer is a complete one rather than one bearing's worth.
        for (let i = 0; i < CONF_BEARINGS; i++) confinementStep(sysCtx, x, z, alt);
        wetLevelsFor(forestOut.closure, confOut.confinement, forestOut.nearM,
          confOut.wallDist, Weather.rainRate);
        return {
          x, z, altM: +alt.toFixed(1),
          creek: +creekOut.prox.toFixed(4),
          creekDistM: +creekOut.dist.toFixed(1),
          stems: forestOut.count,
          closure: +forestOut.closure.toFixed(3),
          treeNearM: +forestOut.nearM.toFixed(2),
          confinement: +confOut.confinement.toFixed(3),
          wallDistM: +confOut.wallDist.toFixed(1),
          wetForest: +wetOut.forest.toFixed(3),
          wetOpen: +wetOut.open.toFixed(3),
          wetER: +wetOut.er.toFixed(3),
          wetSlap: +wetOut.slap.toFixed(3),
        };
      },
      /** The listener's heading, rad. Everything spatial is relative to this. */
      listenYaw: () => listenYaw,
      costMs: () => systemTimings.audio ?? 0,

      // --- the offline render: the only way anyone can HEAR this piece ---------------
      /** Column names for a recorded row, in order. The format, self-described. */
      frameKeys: () => FRAME_KEYS as readonly string[],
      /** The frame the graph was last told to play. Same object every call. */
      frame: () => frame,
      /**
       * Start recording graph instructions. `max` frames, then it stops on its own so a
       * long scenario cannot grow an unbounded array inside the harness's browser.
       */
      record: (max: number) => { recRows = []; recCap = Math.max(0, max | 0); },
      stopRecording: () => { recCap = 0; return recRows.length; },
      recordedCount: () => recRows.length,
      /** The raw rows, for a probe that wants to do its own arithmetic on them. */
      recorded: () => recRows,
      /**
       * Render what was recorded to a 16-bit stereo WAV, base64. No gesture, no device,
       * faster than real time. This is the piece's answer to "nobody has heard it".
       */
      renderMix,
      /**
       * Force which surface recipe the tires use, or `null` for the real ground.
       *
       * Only exists to make the five surfaces comparable back to back in one clip — the
       * brief asks for them to be distinguishable with your eyes shut and no scenario
       * rides all five. Returns the key that took effect.
       */
      overrideSurface: (key: string | null) => {
        if (!key) { surfaceOverride = null; surfaceOverrideKey = ''; return null; }
        const v = VOICES[key];
        if (!v) return null;
        surfaceOverride = v;
        surfaceOverrideKey = key;
        return key;
      },
    };
  },

  /**
   * Sim rate. Only latching — no synthesis decisions here.
   *
   * `SimEvents` is cleared by physics at the top of every step, so a 60 fps frame that
   * spans four sim steps sees at most the last one. A landing and the bottom-out it
   * caused frequently land in different steps of the same frame; reading them here is
   * the difference between hearing both and hearing whichever came last.
   */
  fixedUpdate(dt: number, ctx: SystemContext, input: ControlInput) {
    void dt;
    const ev = ctx.events;
    if (ev.landed >= 0 && ev.landed > pendLanded) pendLanded = ev.landed;
    if (ev.bottomOut > pendBottomOut) pendBottomOut = ev.bottomOut;
    if (ev.crashed) pendCrashed = true;
    if (ev.hopped) pendHopped = true;
    if (ev.scrubImpulse > pendScrub) pendScrub = ev.scrubImpulse;
    // Peak-hold within the frame: a brake stab shorter than a frame still squeals.
    if (input.brakeFront > brakeFrontIn) brakeFrontIn = input.brakeFront;
    if (input.brakeRear > brakeRearIn) brakeRearIn = input.brakeRear;
    if (input.pedal > pedalIn) pedalIn = input.pedal;
  },

  update(dt: number, ctx: SystemContext) {
    const t0 = performance.now();
    if (dt <= 0 || dt > 0.5) dt = 1 / 60;

    const b = ctx.bike;
    const fw = b.front;
    const rw = b.rear;
    const v = Math.max(0, b.speed);
    const wet = clamp01(Weather.wetness);
    const rain = clamp01(Weather.rainRate);

    mix.speedKmh = v * 3.6;
    mix.altitudeM = b.y;
    mix.frontLoadN = fw.load;
    mix.rearLoadN = rw.load;
    mix.frontSlip = fw.slipUsage;
    mix.rearSlip = rw.slipUsage;
    mix.airborne = b.airborne ? 1 : 0;
    mix.wetness = wet;
    mix.rainRate = rain;

    // --- listener frame ---------------------------------------------------------------
    // Damped toward the bike's yaw through the shortest arc, so crossing +/-pi does not
    // spin the whole world through 360 degrees in one frame.
    if (!listenYawInit) { listenYaw = b.yaw; listenYawInit = true; }
    listenYaw += wrapPi(b.yaw - listenYaw) * (1 - Math.exp(-LISTEN_YAW_RATE * dt));
    listenYaw = wrapPi(listenYaw);
    mix.listenYaw = listenYaw;

    // --- surface -------------------------------------------------------------------
    const targF = surfaceOverride ?? VOICE_BY_SURFACE[fw.ground.surface] ?? FALLBACK_VOICE;
    const targR = surfaceOverride ?? VOICE_BY_SURFACE[rw.ground.surface] ?? FALLBACK_VOICE;
    blendVoice(liveFront, targF, dt);
    blendVoice(liveRear, targR, dt);
    mix.surface = surfaceOverride
      ? surfaceOverrideKey
      : SurfaceProps[rw.ground.surface]?.sound ?? 'hardpack';

    // Contact is smoothed, not switched: a wheel skipping across chatter toggles
    // `contact` several times a frame and a hard gate on it buzzes.
    contactF = damp(contactF, fw.contact ? 1 : 0, 26, dt);
    contactR = damp(contactR, rw.contact ? 1 : 0, 26, dt);

    // --- rolling voice: the primary speed cue ---------------------------------------
    // Level saturates rather than growing without bound — a tire at 60 km/h is not
    // five times as loud as at 12, it is brighter and grainier. Speed's job at the top
    // of the range is carried by band centre and grain rate, not by gain.
    const speedTerm = 1 - Math.exp(-v / (SPEED_REF * 0.62));
    const loadF = clamp01(fw.load / LOAD_REF);
    const loadR = clamp01(rw.load / LOAD_REF);
    // Wet dirt is quieter and much duller: the water film fills the voids that make
    // the noise in the first place.
    const wetLevel = 1 - 0.28 * wet;
    const wetBright = 1 - 0.6 * wet;

    const rollF = liveFront.level * speedTerm * (0.3 + 0.8 * loadF) * contactF * wetLevel * 0.72;
    const rollR = liveRear.level * speedTerm * (0.3 + 0.8 * loadR) * contactR * wetLevel;
    mix.tireFront = rollF;
    mix.tireRear = rollR;
    mix.tire = rollF + rollR;

    const bandR = clamp(liveRear.bandHz + liveRear.bandPerMs * v, 90, 9000);
    const bandF = clamp(liveFront.bandHz + liveFront.bandPerMs * v, 90, 9000);
    mix.tireBandHz = bandR;
    mix.tireBandQ = liveRear.bandQ;

    // --- grain ----------------------------------------------------------------------
    // Rate is per metre travelled, so it is speed-correct for free and a stopped bike
    // makes none. Roughness modulates it: the same surface is grainier where the
    // heightfield says it is rougher.
    const roughF = 1 + 2.2 * clamp01(fw.ground.roughness / 0.05);
    const roughR = 1 + 2.2 * clamp01(rw.ground.roughness / 0.05);
    const rateF = liveFront.grainPerM * v * (0.4 + 0.7 * loadF) * contactF * roughF * 0.55;
    const rateR = liveRear.grainPerM * v * (0.4 + 0.7 * loadR) * contactR * roughR;
    let rateTotal = rateF + rateR;
    // Above the ceiling the ear stops resolving individual strikes anyway; the excess
    // becomes brightness on the rolling voice instead of a hundred stolen voices.
    const grainExcess = clamp01((rateTotal - MAX_ONESHOT_HZ) / MAX_ONESHOT_HZ);
    if (rateTotal > MAX_ONESHOT_HZ) rateTotal = MAX_ONESHOT_HZ;
    mix.grainHz = rateTotal;
    mix.grainPitchHz = liveRear.grainHz;

    // --- slip: the limit, made audible ----------------------------------------------
    // Below the gate a tire is doing its job and says nothing extra. Above it the
    // voice both rises and moves up in frequency, and the Q climbs, so approaching the
    // limit is a change in *character* and not just in volume — which is the thing a
    // rider actually feels.
    const slipF = clamp01((fw.slipUsage - SLIP_GATE) / (1 - SLIP_GATE));
    const slipR = clamp01((rw.slipUsage - SLIP_GATE) / (1 - SLIP_GATE));
    const slipAmt = Math.max(slipR, slipF * 0.7);
    const slipVoice = slipR >= slipF * 0.7 ? liveRear : liveFront;
    const slipLoad = slipR >= slipF * 0.7 ? loadR : loadF;
    mix.slip = slipVoice.slipLevel * Math.pow(slipAmt, 1.45) * speedTerm
      * (0.35 + 0.75 * slipLoad) * (1 - 0.2 * wet);
    // A scrub is a deliberate slide to shed speed off a lip, and the sim reports it as
    // its own impulse rather than as slip usage — so it gets folded in here as a short
    // burst on the same voice, which is what it is.
    scrubEnv = damp(scrubEnv, 0, 5.5, dt);
    if (pendScrub > 0.1) scrubEnv = Math.max(scrubEnv, clamp01(pendScrub));
    mix.slip += scrubEnv * slipVoice.slipLevel * 0.34 * speedTerm;
    mix.slipBandHz = clamp(slipVoice.slipHz * (0.78 + 0.55 * slipAmt + 0.4 * scrubEnv) + 9 * v, 120, 9000);

    // A locked wheel is not a scrub. `slipRatio` at -1 with the brake on is a skid:
    // narrower, lower, and it does not care how hard you are cornering.
    const lockR = clamp01((-rw.slipRatio - 0.55) / 0.45) * clamp01(brakeRearIn * 1.4);
    const lockF = clamp01((-fw.slipRatio - 0.55) / 0.45) * clamp01(brakeFrontIn * 1.4);
    mix.skid = Math.max(lockR, lockF * 0.8) * speedTerm * 0.5 * slipVoice.slipLevel;
    mix.wetHiss = wet * speedTerm * 0.16 * Math.max(contactF, contactR);

    // --- suspension ------------------------------------------------------------------
    // A shock's voice is its shim stack passing oil, so the continuous level is the
    // rod velocity, not the position. Compression and rebound are separate voices
    // because they genuinely sound different: compression is dark and loaded, rebound
    // is a lighter, higher tick as the spring pushes the wheel back down.
    const compVel = Math.max(0, fw.travelVel) + Math.max(0, rw.travelVel);
    const rebVel = Math.max(0, -fw.travelVel) + Math.max(0, -rw.travelVel);
    const compT = clamp01(compVel / SUS_VEL_REF);
    const rebT = clamp01(rebVel / SUS_VEL_REF);
    // Fast attack, slow release: a damper is a whoosh, not a click.
    suspCompS = compT > suspCompS ? compT : damp(suspCompS, compT, 9, dt);
    suspRebS = rebT > suspRebS ? rebT : damp(suspRebS, rebT, 7, dt);
    mix.suspComp = suspCompS * 0.5;
    mix.suspReb = suspRebS * 0.3;
    mix.susp = mix.suspComp + mix.suspReb;

    // --- chatter, chain, drivetrain ---------------------------------------------------
    // Chain slap is not suspension travel, it is the *high-frequency part* of it: the
    // chain rattles over stutter bumps and goes quiet through a big smooth compression.
    // Differencing against a slow mean is what separates the two.
    const travelEnergy = Math.abs(fw.travelVel) + Math.abs(rw.travelVel);
    chatterSlow = damp(chatterSlow, travelEnergy, 3.5, dt);
    chatterS = damp(chatterS, Math.abs(travelEnergy - chatterSlow), 16, dt);
    const chatter = clamp01(chatterS / 0.55);
    mix.chain = chatter * (0.25 + 0.75 * clamp01(v / 8)) * 0.24
      + (b.airborne ? 0.08 : 0) * clamp01(b.airTime * 2);
    mix.drivetrain = clamp01(pedalIn) * clamp01(v / 4) * (1 - clamp01(v / 22)) * 0.12;

    // --- brake squeal -----------------------------------------------------------------
    // Squeal is a resonance, and a resonance needs relative motion: a locked wheel is
    // silent and a free wheel with the lever pulled is silent. It lives in the narrow
    // band where the pad is loaded and the rotor is still turning, which is exactly
    // where a rider is modulating at the limit.
    const brakeCmd = Math.max(brakeFrontIn, brakeRearIn * 0.8);
    const rotorMoving = clamp01(v / 3.5) * (1 - clamp01(Math.max(lockF, lockR) * 1.3));
    const nearLimit = smoothstep(0.35, 0.85, brakeCmd);
    mix.brake = nearLimit * rotorMoving * (0.55 + 0.45 * Math.max(slipF, slipR)) * 0.2
      * (1 + 0.4 * wet);

    // --- wind -------------------------------------------------------------------------
    // Squared-ish in speed because that is what pressure does, and the band centre
    // rises so that fast reads as a shriek past the helmet rather than a louder hum.
    const windT = clamp01((v - WIND_ON) / WIND_SPAN);
    mix.wind = Math.pow(windT, 1.7) * 0.62;
    mix.windBandHz = clamp(300 + 26 * v, 200, 2400);

    // --- impacts ----------------------------------------------------------------------
    // Severity does two things at once: it raises the level and it drops the pitch. A
    // landing that is only louder reads as a mistake in the mix; one that is also
    // lower reads as *heavy*, which is what BAR asks for.
    impactEnv = damp(impactEnv, 0, 3.4, dt);
    impactTimer -= dt;
    bottomTimer -= dt;
    let firedImpact = -1;
    let firedBottom = 0;
    if (pendLanded >= 0) mix.impactRaw++;
    if (pendLanded >= IMPACT_GATE && impactTimer <= 0) {
      const sev = clamp01(pendLanded);
      firedImpact = sev;
      // A big landing owns the next third of a second: the small re-contacts that
      // follow it are the same event, not four more landings.
      impactTimer = IMPACT_REFRACTORY + 0.35 * sev;
      mix.lastImpact = sev;
      mix.impactCount++;
      impactEnv = Math.max(impactEnv, 0.35 + 0.65 * sev);
    }
    if (pendBottomOut > 0.02) mix.bottomOutRaw++;
    // Re-arm only once the damper has come off its stop: on this build it can sit
    // there for a second at a time, and a timer alone would just clank rhythmically.
    if (pendBottomOut < BOTTOM_GATE * 0.4) bottomArmed = true;
    if (pendBottomOut >= BOTTOM_GATE && bottomArmed && bottomTimer <= 0) {
      firedBottom = clamp01(pendBottomOut);
      bottomArmed = false;
      bottomTimer = BOTTOM_REFRACTORY + 0.2 * firedBottom;
      mix.bottomOutCount++;
      impactEnv = Math.max(impactEnv, 0.3 + 0.5 * firedBottom);
    }
    if (pendCrashed) impactEnv = 1;
    mix.impact = impactEnv;

    // --- ambience ---------------------------------------------------------------------
    ambTimer -= dt;
    gustPhase += dt * 0.31;
    burblePhase += dt * 0.7;
    if (ambTimer <= 0) {
      ambTimer = 1 / AMB_HZ;
      creekAt(ctx, b.x, b.z, b.y);
      creekProx = creekOut.prox;
      forestAt(b.x, b.z);
      // Two bearings a tick: the ring is 8 wide and refreshes in ~0.7 s, which is faster
      // than the landform under a bike can change.
      confinementStep(ctx, b.x, b.z, b.y);
      confinementStep(ctx, b.x, b.z, b.y);
    }
    // Every space driver is a room, and a room that steps is worse than a room that is
    // slightly late. All of them are damped, and everything downstream reads the damped
    // value — including the delay times, which are then swept at TAU_DELAY on top.
    forestClosureS = damp(forestClosureS, forestOut.closure, 1.7, dt);
    treeNearS = damp(treeNearS, forestOut.nearM, 1.4, dt);
    confinementS = damp(confinementS, confOut.confinement, 0.8, dt);
    wallDistS = damp(wallDistS, confOut.wallDist, 0.6, dt);
    wallDirXS = damp(wallDirXS, confOut.wallDirX, 1.1, dt);
    wallDirZS = damp(wallDirZS, confOut.wallDirZ, 1.1, dt);
    if (creekOut.found) {
      // A fresh estimate more than 120 m from the tracked one is a different channel,
      // not the same one moving.
      const jumped = Math.hypot(creekOut.wx - creekWX, creekOut.wz - creekWZ) > 120;
      if (!creekHave) {
        creekWX = creekOut.wx;
        creekWZ = creekOut.wz;
        creekHave = true;
      } else if (jumped) {
        creekRelocate = true;
      } else if (!creekRelocate) {
        creekWX = damp(creekWX, creekOut.wx, 1.1, dt);
        creekWZ = damp(creekWZ, creekOut.wz, 1.1, dt);
      }
    }
    // Fade out, move, fade back: ~150 ms down and ~330 ms up. The move happens under
    // the level, so the image never travels.
    if (creekRelocate) {
      creekFade = damp(creekFade, 0, 7, dt);
      if (creekFade < 0.04) {
        creekWX = creekOut.wx;
        creekWZ = creekOut.wz;
        creekRelocate = false;
      }
    } else {
      creekFade = damp(creekFade, 1, 3, dt);
    }
    mix.forestClosure = forestClosureS;
    mix.treeStems = forestOut.count;
    mix.treeNearM = treeNearS;
    mix.confinement = confinementS;
    mix.wallDistM = wallDistS;

    const canopy = canopyAt(b.y);
    mix.canopy = canopy;
    const gust = 1 + 0.42 * Math.sin(gustPhase) * Math.sin(gustPhase * 0.43 + 1.7);
    const windNorm = clamp01((Weather.windSpeed * gust) / 12);
    mix.ambTrees = canopy * (0.16 + 0.62 * windNorm) * 0.34;
    mix.ambAlpine = (1 - canopy) * (0.1 + 0.85 * windNorm) * 0.26;
    const birdGate = canopy * birdActivity(Weather.timeOfDay, rain)
      * (1 - clamp01((v - 2) / 8));
    mix.ambBirds = birdGate * 0.34;
    // The creek is now a source at a range, not a level. Distance takes the level down
    // on a 1/(1+d/d0) law and takes the top off it — water two hundred metres away
    // through timber is a low roar, water you are standing over is a hiss with stones
    // in it. Both halves are audible while you ride toward it, which is the point.
    // Range and bearing are both recomputed from the tracked world point every frame, so
    // riding past the water sweeps it around you instead of stepping it at the probe rate.
    const creekDX = creekHave ? creekWX - b.x : 0;
    const creekDZ = creekHave ? creekWZ - b.z : 0;
    creekDistS = creekHave ? Math.hypot(creekDX, creekDZ) : 0;
    const creekFar = 1 / (1 + creekDistS / CREEK_HALF_M);
    mix.ambCreek = creekProx * 0.42 * (1 + 0.35 * wet) * creekFar * creekFade;
    const creekAudible = mix.ambCreek > 0.0015;
    mix.creekDistM = creekAudible ? creekDistS : 0;
    const creekCut = 700 + 4600 * Math.exp(-creekDistS / CREEK_DULL_M);
    // Bearing: the tracked channel point, rotated into the listener's frame.
    placeWorld(creekDX, creekDZ, SPREAD_WORLD);
    // Standing in the channel, the water is on both sides of you; from the shoulder it
    // is a direction. Collapse the pan toward centre as the range goes to zero.
    const creekNearness = clamp01(creekDistS / 45);
    /** 1 when you are standing over the water, 0 at range. The stone burble is the part
     *  of a creek that does not carry — you only get it up close. */
    const creekNearnessInv = 1 - creekNearness;
    // Silent water has no bearing. Collapsing the pan while it is inaudible keeps it
    // from wandering off-centre with nothing playing and then sweeping back across the
    // image the moment it fades in.
    const creekPan = creekAudible ? place.pan * creekNearness : 0;
    const creekTilt = creekAudible ? place.tiltDb * creekNearness : 0;
    mix.panCreek = creekPan;
    mix.creekBearingDeg = creekAudible ? placeBearingDeg() : 0;
    mix.ambRain = rain * 0.4;
    mix.amb = mix.ambTrees + mix.ambAlpine + mix.ambCreek + mix.ambRain;

    // --- the room ----------------------------------------------------------------------
    // Three wet sends, and the whole reason a summit and a stand of timber and a
    // drainage are three different places rather than three levels of the same one.
    //
    //  - forest:  measured stems. Dense, dark, short. Dies in a clearing.
    //  - open:    the landform's enclosure with the trees taken *out* — a bowl above the
    //             treeline is the biggest reverb in the game and a bowl full of firs is
    //             not, because the trunks have already eaten the tail.
    //  - ER:      discrete trunk returns at their real range.
    //  - slap:    one echo off the steepest horizon, from its bearing.
    //
    // Rain adds a little to all of it. `wetLevelsFor` is the same function the offline
    // render probe drives, so what is measured there is what plays here.
    wetLevelsFor(forestClosureS, confinementS, treeNearS, wallDistS, rain);
    mix.wetForest = wetOut.forest;
    mix.wetOpen = wetOut.open;
    mix.wetER = wetOut.er;
    mix.wetSlap = wetOut.slap;
    const slapS = wetOut.slapS;
    mix.erMs = wetOut.erBaseS * 1000;
    mix.slapMs = mix.wetSlap > 0.001 ? slapS * 1000 : 0;
    // Where the wall is, in the listener's frame — the slap comes back from over there.
    placeWorld(wallDirXS, wallDirZS, SPREAD_WORLD);
    const slapPan = place.pan;
    mix.wallBearingDeg = wallDistS > 1 ? placeBearingDeg() : 0;
    // One number for "is this a place": total wet weighted by the send each bus gets,
    // over the dry sum. ~0 on a bare summit; a few tenths in closed timber.
    const drySum = mix.tire + mix.slip + mix.skid + mix.susp + mix.chain + mix.wind
      + mix.amb + impactEnv * 0.5 + 0.02;
    const sendSum = (mix.tire + mix.slip + mix.skid) * SEND_TIRE
      + (mix.susp + mix.chain + mix.drivetrain + mix.brake) * SEND_MECH
      + impactEnv * 0.5 * SEND_IMPACT + mix.wind * SEND_WIND
      + (mix.ambTrees + mix.ambAlpine + mix.ambRain) * SEND_AMB
      + (mix.ambCreek + mix.ambBirds) * SEND_AMBFAR;
    mix.wetDry = (sendSum * (mix.wetForest + mix.wetOpen + mix.wetER + mix.wetSlap)
      * WET_MASTER) / drySum;

    // --- ducking ----------------------------------------------------------------------
    // The tire is the protected voice. Everything atmospheric gets out of its way in
    // proportion to how loud it currently is, which is why the mountain gets quiet as
    // you pick up speed and comes back the moment you stop.
    const tireLoud = mix.tire + mix.slip * 0.8 + mix.skid * 0.6;
    mix.duckAmb = 1 / (1 + 3.2 * tireLoud + 2.4 * mix.wind);
    mix.duckWind = 1 / (1 + 0.9 * impactEnv);
    mix.duckMech = 1 / (1 + 1.0 * impactEnv);
    // The room is ducked too, and gently: enough that a tail cannot smear the primary
    // speed cue at 60 km/h, not so much that the forest disappears the moment you ride.
    // Hard-ducking the reverb was the obvious move and it undoes the whole piece — the
    // place is supposed to be there while you are moving through it.
    const duckWet = 1 / (1 + 0.85 * tireLoud);
    mix.master = MASTER_GAIN * (enabled ? 1 : 0) * (1 - 0.22 * clamp01(impactEnv));

    // --- the image ----------------------------------------------------------------------
    // Contact patches are placed at the sim's own world contact points, so nothing here
    // is authored: a crossed-up bike images itself, and the roost goes where the tire
    // went. Bike sources use the narrower spread — they are inside your own arms.
    placeWorld(fw.cx - b.x, fw.cz - b.z, SPREAD_BIKE);
    const panF = place.pan;
    const tiltF = place.tiltDb;
    const trimF = place.trim;
    mix.panTireF = panF;

    placeWorld(rw.cx - b.x, rw.cz - b.z, SPREAD_BIKE);
    const panR = place.pan;
    const tiltR = place.tiltDb;
    const trimR = place.trim;
    mix.panTireR = panR;
    mix.backTireR = place.back;

    // Slip and skid come off whichever end is actually letting go, so the scrub is at
    // that wheel's azimuth. Widened past the geometric answer, because a slide is the
    // one moment a rider is entitled to know exactly where the bike is without looking.
    const slipRear = slipR >= slipF * 0.7;
    const panSlip = clamp((slipRear ? panR : panF) * 1.5, -1, 1);
    mix.panSlip = panSlip;
    const tiltSlip = slipRear ? tiltR : tiltF;

    // The chain is on the drive side and always has been. It is a small thing and it is
    // the kind of small thing that makes a mix sound like it was made by someone who
    // has looked at a bicycle.
    placeLocal(0.19, -0.15, SPREAD_BIKE);
    const panChain = place.pan;
    mix.panChain = panChain;

    // Wind past the helmet is at your ears, not in the world — so it stays wide rather
    // than being placed. A crosswind biases it: relative bearing of the airflow shifts
    // the pair toward the ear it is hitting.
    const relWindX = Weather.windX;
    const relWindZ = Weather.windZ;
    placeWorld(relWindX, relWindZ, 1);
    const windBias = place.sinAz * 0.3 * clamp01(Weather.windSpeed / 12);
    mix.panWind = windBias;

    // --- fill the frame ------------------------------------------------------------------
    // Unconditionally, whether or not an audio device exists.
    //
    // This is the change that closes round 2's gap. Everything the graph is about to be
    // told is written into one flat record first, and the record is computed by the model
    // rather than by the renderer. So the capture harness — which never gestures and
    // therefore never has an AudioContext — still produces a complete, frame-accurate
    // description of the mix, which can be recorded, carried out of the browser, and
    // replayed into an OfflineAudioContext to make a file a human can play. It costs a few
    // dozen multiplies a frame and it is the difference between a piece nobody has heard
    // and a piece that ships a WAV with its report.
    // How far the tire gets out of a landing's way. It was 1.5, which is 8 dB at full
    // severity — and with this build landing eight times in ten seconds, `impactEnv`
    // (0.3 s time constant) is high for most of a run, so the *protected* voice was the
    // one being ducked, nearly continuously, by the voice it is supposed to outrank.
    // 0.45 is 3.2 dB at worst: enough that a landing has room, not enough to take the
    // speed cue away while the bike is on the ground.
    const tireDuck = 1 / (1 + 0.45 * impactEnv);
    const md = mix.duckMech;
    const wd = mix.duckWind;
    const ad = mix.duckAmb;
    // The two halves of each bed run on gust phases a couple of seconds apart, so a gust
    // arrives on one side and crosses to the other. That is the cheapest thing in this
    // file and the one that most makes the forest a place rather than a hiss.
    const gustR = 1 + 0.42 * Math.sin(gustPhase + 2.4) * Math.sin(gustPhase * 0.43 + 0.4);
    const windNormR = clamp01((Weather.windSpeed * gustR) / 12);
    // The right half of the canopy bed is the same bed evaluated at its own gust, which is
    // the ratio of the two level curves. Round 2 wrote this as a raw ratio of the two wind
    // norms with a 0.001 floor under the denominator — in still air that is a division by
    // 0.001, and the right channel could be told to play at fourteen times the level of the
    // left. Nobody caught it because nobody has ever heard this file. Written this way the
    // scale is bounded by construction: 0.16 is the bed's own still-air term.
    const treesRScale = (0.16 + 0.62 * windNormR) / (0.16 + 0.62 * windNorm);
    const panBrake = brakeFrontIn >= brakeRearIn ? panF : panR;

    const f = frame;
    f.dt = dt;

    f.tireFLevel = rollF * tireDuck * trimF;
    f.tireFHz = bandF;
    f.tireFQ = liveFront.bandQ;
    f.tireFShelf = liveFront.brightDb * wetBright + 5 * grainExcess + tiltF;
    f.tireFPan = panF;
    f.tireRLevel = rollR * tireDuck * trimR;
    f.tireRHz = bandR;
    f.tireRQ = liveRear.bandQ;
    f.tireRShelf = liveRear.brightDb * wetBright + 5 * grainExcess + tiltR;
    f.tireRPan = panR;
    f.slipLevel = mix.slip * tireDuck;
    f.slipHz = mix.slipBandHz;
    f.slipQ = slipVoice.slipQ * (0.8 + 1.6 * slipAmt);
    f.slipShelf = tiltSlip;
    f.slipPan = panSlip;
    f.skidLevel = mix.skid * tireDuck;
    f.skidHz = 900 + 40 * v;
    f.skidShelf = tiltR;
    f.skidPan = panR;
    f.wetHissLevel = mix.wetHiss;
    f.wetHissPan = panR * 0.5;

    // The fork is ahead of you and the shock is under you; that is the whole reason
    // compression and rebound get different pans rather than the same centre.
    f.compLevel = mix.suspComp * md;
    f.compHz = 420 + 700 * suspCompS;
    f.compPan = panF * 0.4;
    f.rebLevel = mix.suspReb * md;
    f.rebHz = 1200 + 900 * suspRebS;
    f.rebPan = panR * 0.4;
    f.chainLevel = mix.chain * md;
    f.chainHz = 3600 + 1400 * chatter;
    f.chainPan = panChain;
    f.driveLevel = mix.drivetrain * md;
    f.driveHz = 620 + 46 * v;
    // The two peaks drift against each other so the squeal warbles like a real one, and
    // they sit at the wheel whose lever is pulled hardest.
    f.brakeALevel = mix.brake * md;
    f.brakeAHz = 2500 + 240 * Math.sin(gustPhase * 5.3);
    f.brakeBLevel = mix.brake * md * 0.7;
    f.brakeBHz = 3900 + 320 * Math.sin(gustPhase * 3.1 + 2);
    f.brakePan = panBrake;

    f.windALevel = mix.wind * wd;
    f.windAHz = mix.windBandHz;
    f.windAPan = -0.5 + windBias;
    f.windBLevel = mix.wind * wd * 0.6;
    f.windBHz = 170 + 8 * v;
    f.windBPan = 0.5 + windBias;

    f.treesLLevel = mix.ambTrees * ad;
    f.treesLHz = 700 + 500 * windNorm;
    f.treesRLevel = mix.ambTrees * ad * treesRScale;
    f.treesRHz = 700 + 500 * windNormR;
    f.alpineLLevel = mix.ambAlpine * ad;
    f.alpineLHz = 1500 + 900 * windNorm;
    f.alpineRLevel = mix.ambAlpine * ad;
    f.alpineRHz = 1500 + 900 * windNormR;
    f.creekLevel = mix.ambCreek * ad;
    f.creekHz = creekCut;
    f.creekTilt = creekTilt;
    f.creekPan = creekPan;
    f.burbleLevel = mix.ambCreek * ad * 0.5 * creekNearnessInv;
    f.burbleHz = 520 + 220 * Math.sin(burblePhase) * Math.sin(burblePhase * 0.37);
    f.rainLLevel = mix.ambRain * ad;
    f.rainRLevel = mix.ambRain * ad;

    f.wetForest = mix.wetForest;
    f.wetOpen = mix.wetOpen;
    f.wetER = mix.wetER;
    f.erBaseS = wetOut.erBaseS;
    f.wetSlap = mix.wetSlap;
    f.slapS = slapS;
    f.slapPan = slapPan;
    // A return from 150 m has lost its top to the air; one from 20 m has not.
    f.slapLpHz = clamp(5200 * Math.exp(-wallDistS / 150) + 700, 300, 9000);
    f.wetOutGain = WET_MASTER * duckWet;
    f.masterGain = mix.master;

    f.grainRateF = rateF;
    f.grainRateR = rateR;
    f.grainLoadF = loadF;
    f.grainLoadR = loadR;
    f.grainWet = wet;
    f.grainFHz = liveFront.grainHz;
    f.grainFQ = liveFront.grainQ;
    f.grainFDecay = liveFront.grainDecay;
    f.grainFLevel = liveFront.grainLevel;
    f.grainRHz = liveRear.grainHz;
    f.grainRQ = liveRear.grainQ;
    f.grainRDecay = liveRear.grainDecay;
    f.grainRLevel = liveRear.grainLevel;
    f.chainRate = chatter * (2 + 9 * clamp01(v / 10));
    f.chatter = chatter;
    f.firedImpact = firedImpact;
    f.firedBottom = firedBottom;
    f.crashed = pendCrashed ? 1 : 0;
    f.hopped = pendHopped ? 1 : 0;
    f.impactSlapHz = liveRear.bandHz * 1.4;
    f.impactGritHz = liveRear.grainHz;
    f.impactGritQ = liveRear.grainQ;
    f.birdGate = birdGate;
    f.birdLevel = mix.ambBirds * ad;
    f.rainTickRate = rain * 26;
    f.rainTickLevel = 0.05 * rain * ad;

    f.speedKmh = mix.speedKmh;
    f.surfaceIdx = rw.ground.surface;
    f.altitudeM = b.y;
    f.rearSlip = rw.slipUsage;
    f.airborne = b.airborne ? 1 : 0;

    if (recCap > 0 && recRows.length < recCap) recRows.push(frameToRow(f));

    // --- render to the graph ------------------------------------------------------------
    // One call. Everything below this line used to live inline here and could therefore
    // only ever run against a live device; it is now the same function an offline render
    // drives, which is the only reason a file exists to listen to.
    if (graph && graph.ac.state === 'running') {
      mix.ctxState = graph.ac.state;
      mix.sampleRate = graph.ac.sampleRate;
      renderFrame(graph, f, graph.ac.currentTime, liveRS);
      mix.voicesActive = liveRS.active;
      mix.voicesDropped = liveRS.dropped;
    } else {
      mix.ctxState = graph ? graph.ac.state : (buildFailed ? 'failed' : 'none');
      mix.sampleRate = graph ? graph.ac.sampleRate : 0;
      // Keep the accumulators from winding up while silent, so the first frame after a
      // gesture does not fire two hundred grains at once.
      liveRS.grainAccF = liveRS.grainAccR = liveRS.chainAcc = liveRS.rainAcc = 0;
      mix.voicesActive = 0;
    }

    // Consume the latched sim events and inputs.
    pendLanded = -1;
    pendBottomOut = 0;
    pendCrashed = false;
    pendHopped = false;
    pendScrub = 0;
    brakeFrontIn = 0;
    brakeRearIn = 0;
    pedalIn = 0;

    mix.cpuMs = performance.now() - t0;
  },

  dispose() {
    graph?.dispose();
    graph = null;
    liveAc = null;
  },
});

function countActive(pool: NoiseSlot[], now: number): number {
  let n = 0;
  for (let i = 0; i < pool.length; i++) if (pool[i].freeAt > now) n++;
  return n;
}

/**
 * One frame of instructions -> one graph. The only function in this file that writes to
 * an audio node, and it does not know or care whether that node is going to a speaker.
 *
 * `now` is the destination's clock, not the wall clock: live, it is
 * `AudioContext.currentTime`; offline, it is the running sum of the recorded `dt`, so a
 * 22 s scenario schedules 22 s of automation in a couple of seconds of CPU and comes back
 * as samples. `st` carries the accumulators and the RNG, so a render never disturbs the
 * live graph's scheduling and is reproducible from the same rows.
 */
function renderFrame(g: AudioGraph, f: AudioFrame, now: number, st: RenderState): void {
  const rnd = st.rnd;
  const dt = f.dt;

  setVoice(g.vTireF, f.tireFLevel, f.tireFHz, f.tireFQ, f.tireFShelf, f.tireFPan, now);
  setVoice(g.vTireR, f.tireRLevel, f.tireRHz, f.tireRQ, f.tireRShelf, f.tireRPan, now);
  setVoice(g.vSlip, f.slipLevel, f.slipHz, f.slipQ, f.slipShelf, f.slipPan, now);
  setVoice(g.vSkid, f.skidLevel, f.skidHz, 1.2, f.skidShelf, f.skidPan, now);
  setVoice(g.vWet, f.wetHissLevel, 2600, 0.7, 0, f.wetHissPan, now);

  setVoice(g.vComp, f.compLevel, f.compHz, 0.9, 0, f.compPan, now);
  setVoice(g.vReb, f.rebLevel, f.rebHz, 1.6, 0, f.rebPan, now);
  setVoice(g.vChain, f.chainLevel, f.chainHz, 2.2, 0, f.chainPan, now);
  setVoice(g.vDrive, f.driveLevel, f.driveHz, 8, 0, f.chainPan, now);
  setVoice(g.vBrake1, f.brakeALevel, f.brakeAHz, 26, 0, f.brakePan, now);
  setVoice(g.vBrake2, f.brakeBLevel, f.brakeBHz, 31, 0, f.brakePan * 0.8, now);

  setVoice(g.vWindBand, f.windALevel, f.windAHz, 0.8, 0, f.windAPan, now);
  setVoice(g.vWindRoar, f.windBLevel, f.windBHz, 0.9, 0, f.windBPan, now);

  setVoice(g.vTrees, f.treesLLevel, f.treesLHz, 0.6, 0, -0.62, now);
  setVoice(g.vTreesR, f.treesRLevel, f.treesRHz, 0.6, 0, 0.62, now);
  setVoice(g.vAlpine, f.alpineLLevel, f.alpineLHz, 0.6, 0, -0.78, now);
  setVoice(g.vAlpineR, f.alpineRLevel, f.alpineRHz, 0.6, 0, 0.78, now);
  setVoice(g.vCreek, f.creekLevel, f.creekHz, 0.8, f.creekTilt, f.creekPan, now);
  setVoice(g.vCreekBurble, f.burbleLevel, f.burbleHz, 5, f.creekTilt, -f.creekPan * 0.6, now);
  setVoice(g.vRain, f.rainLLevel, 1300, 0.7, 0, -0.55, now);
  setVoice(g.vRainR, f.rainRLevel, 1450, 0.7, 0, 0.55, now);

  // --- the room ---------------------------------------------------------------------
  const sp = g.space;
  setParam(sp.gForest.gain, f.wetForest, now, TAU_LEVEL);
  setParam(sp.gOpen.gain, f.wetOpen, now, TAU_LEVEL);
  for (let i = 0; i < sp.erDelay.length; i++) {
    setParam(sp.erDelay[i].delayTime, f.erBaseS * ER_MUL[i], now, TAU_DELAY);
    setParam(sp.erGain[i].gain, f.wetER * (1 - i * 0.17), now, TAU_LEVEL);
    // Later taps have bounced off more trunks and lost more top each time.
    setParam(sp.erLp[i].frequency, 3800 - i * 620, now, TAU_FILTER);
  }
  setParam(sp.slapDelay.delayTime, f.slapS, now, TAU_DELAY);
  setParam(sp.slapGain.gain, f.wetSlap, now, TAU_LEVEL);
  setParam(sp.slapPan.pan, clamp(f.slapPan, -1, 1), now, TAU_PAN);
  setParam(sp.slapLp.frequency, f.slapLpHz, now, TAU_FILTER);
  setParam(sp.out.gain, f.wetOutGain, now, TAU_LEVEL);

  setParam(g.master.gain, f.masterGain, now, 0.05);

  // --- one-shots ---------------------------------------------------------------------
  st.active = countActive(g.grainPool, now) + countActive(g.mechPool, now)
    + countActive(g.impactPool, now) + countActive(g.ambPool, now);

  // Grains, front and rear kept apart so a front wheel finding a root and a rear wheel
  // on gravel are two different sounds at once, which is what they are.
  st.grainAccF += f.grainRateF * dt;
  st.grainAccR += f.grainRateR * dt;
  let guard = 0;
  while (st.grainAccF >= 1 && guard++ < 8) {
    st.grainAccF -= 1;
    fireGrain(g, st, f.grainFHz, f.grainFQ, f.grainFDecay, f.grainFLevel, f.grainLoadF,
      f.grainWet, f.tireFPan, now + rnd() * dt * 0.8);
  }
  guard = 0;
  while (st.grainAccR >= 1 && guard++ < 8) {
    st.grainAccR -= 1;
    fireGrain(g, st, f.grainRHz, f.grainRQ, f.grainRDecay, f.grainRLevel, f.grainLoadR,
      f.grainWet, f.tireRPan, now + rnd() * dt * 0.8);
  }
  if (st.grainAccF > 8) st.grainAccF = 8;
  if (st.grainAccR > 8) st.grainAccR = 8;

  // Chain slap: discrete metallic clinks whose rate follows chatter, on top of the
  // continuous rattle bed above. On the drive side, like the bed.
  st.chainAcc += f.chainRate * dt;
  guard = 0;
  while (st.chainAcc >= 1 && guard++ < 4) {
    st.chainAcc -= 1;
    const s = claim(g.mechPool, now);
    if (!s) { st.dropped++; break; }
    pluck(s, 3200 + rnd() * 3000, 18 + rnd() * 14,
      0.09 * f.chatter * (0.5 + rnd()), 0.001, 0.035 + rnd() * 0.05,
      f.chainPan + (rnd() - 0.5) * 0.2, now);
  }
  if (st.chainAcc > 3) st.chainAcc = 3;

  // Bottom-out: a metallic clank, not a thud. Two high-Q peaks an octave-ish apart ring
  // inharmonically, which is what a bump stop and a lower leg do. It is the fork and the
  // shock, so it images between the two ends rather than at a point.
  if (f.firedBottom > 0) {
    const sev = f.firedBottom;
    const s1 = claim(g.mechPool, now);
    if (s1) pluck(s1, 1750 + rnd() * 300, 26, 0.3 * sev, 0.0012, 0.19 + 0.2 * sev,
      f.tireFPan * 0.6, now);
    const s2 = claim(g.mechPool, now);
    if (s2) pluck(s2, 2680 + rnd() * 420, 30, 0.19 * sev, 0.0012, 0.12 + 0.14 * sev,
      f.tireRPan * 0.6, now);
    const t = claimTone(g.thumpPool, now);
    if (t) thump(t, 128 - 40 * sev, 58 - 10 * sev, 0.26 * sev, 0.003, 0.14 + 0.18 * sev,
      0, now);
  }

  // Landing. Three components, all scaled by severity, and severity moves the pitch down
  // as well as the level up — that is what makes a big one *heavy* rather than just loud.
  // The body stays centred (it is the whole bike and the rider's own legs, and a low
  // thump has no image anyway); the tire slap is at the rear contact patch.
  if (f.firedImpact >= 0) {
    const sev = f.firedImpact;
    const t = claimTone(g.thumpPool, now);
    if (t) {
      // The pitch floor is 46 Hz rather than 33: the master highpass sits at 38 Hz, so a
      // thump that ended at 33 was spending the last third of its decay under the filter
      // — all of the cost of a big landing and none of the weight. 46 Hz lands in the
      // octave a laptop can actually move.
      thump(t, lerp(150, 62, sev), lerp(84, 46, sev), lerp(0.16, 0.72, sev),
        0.004, lerp(0.13, 0.62, sev), 0, now);
    }
    const body = claim(g.impactPool, now);
    if (body) {
      pluck(body, lerp(900, 330, sev), 0.9, lerp(0.12, 0.5, sev), 0.002,
        lerp(0.09, 0.34, sev), 0, now);
    }
    // The tires hit too, in the voice of whatever they hit.
    const slap = claim(g.impactPool, now);
    if (slap) {
      pluck(slap, f.impactSlapHz, 1.2, lerp(0.08, 0.34, sev), 0.0015,
        lerp(0.05, 0.16, sev), f.tireRPan, now);
    }
    if (sev > 0.55) {
      const grit = claim(g.impactPool, now);
      if (grit) pluck(grit, f.impactGritHz, f.impactGritQ, 0.22 * sev, 0.002, 0.16,
        f.tireRPan * 0.8, now);
    }
  }
  if (f.crashed > 0) {
    const t = claimTone(g.thumpPool, now);
    if (t) thump(t, 68, 42, 0.7, 0.005, 0.75, 0, now);
    const s = claim(g.impactPool, now);
    if (s) pluck(s, 260, 0.6, 0.5, 0.003, 0.55, f.tireRPan * 0.5, now);
  }
  if (f.hopped > 0) {
    const s = claim(g.mechPool, now);
    if (s) pluck(s, 2400, 12, 0.06, 0.002, 0.06, f.chainPan * 0.7, now);
  }

  // Birds. Scheduled, not looped: an interval drawn per call, so the same three notes
  // never come back on a beat — and each phrase is drawn a fresh azimuth, so birds are
  // *around* you in the canopy rather than a mono chirp at the centre of your head.
  st.birdTimer -= dt;
  if (st.birdTimer <= 0) {
    st.birdTimer = f.birdGate > 0.02
      ? (0.9 + rnd() * 4.5) / Math.max(f.birdGate, 0.06)
      : 2 + rnd() * 3;
    if (f.birdGate > 0.05) chirp(g, st, now, f.birdLevel);
  }

  // Rain on the rider: sparse ticks over the continuous hiss, scattered across the image
  // because drops do not all land in the same place.
  st.rainAcc += f.rainTickRate * dt;
  guard = 0;
  while (st.rainAcc >= 1 && guard++ < 4) {
    st.rainAcc -= 1;
    const s = claim(g.ambPool, now);
    if (!s) break;
    pluck(s, 2200 + rnd() * 3500, 9, f.rainTickLevel, 0.001,
      0.012 + rnd() * 0.02, rnd() * 1.7 - 0.85, now);
  }
  if (st.rainAcc > 3) st.rainAcc = 3;
}

/**
 * One grain: a stone tick, a root knock, a scuff of hardpack.
 *
 * Hoisted to module scope rather than closed over the update's locals because the repo
 * rule is zero allocation in per-frame paths, and a closure declared inside update() is
 * one object per frame at 120 fps.
 */
function fireGrain(
  g: AudioGraph, st: RenderState, grainHz: number, grainQ: number, grainDecay: number,
  grainLevel: number, load: number, wet: number, pan: number, when: number,
): void {
  const rnd = st.rnd;
  const s = claim(g.grainPool, when);
  if (!s) { st.dropped++; return; }
  const hz = grainHz * (0.72 + 0.62 * rnd());
  const pk = grainLevel * (0.45 + 0.75 * load) * (0.6 + 0.55 * rnd()) * (1 - 0.35 * wet);
  // Jittered around the wheel's own azimuth. A stone is thrown from the contact patch,
  // not from a point: without the jitter forty grains a second stack on one pan value
  // and collapse the roost back into the mono blob this piece is here to fix.
  pluck(s, hz, grainQ, pk, 0.0016, grainDecay * (0.7 + 0.6 * rnd()),
    clamp(pan + (rnd() - 0.5) * 0.5, -1, 1), when);
}

/**
 * A short phrase of 2-4 notes with a pitch drop on each. Two birds never agree.
 *
 * One azimuth per phrase, drawn fresh — a bird sings from where it is perched and does
 * not move between notes. Sources behind get the same shelf-free treatment the rest of
 * the far ambience does, but the pan alone carries most of it here because a chirp is
 * almost all high frequency, which is where interaural level difference works best.
 */
function chirp(g: AudioGraph, st: RenderState, now: number, level: number): void {
  const rnd = st.rnd;
  const notes = 2 + Math.floor(rnd() * 3);
  const az = rnd() * Math.PI * 2;
  const pan = Math.sin(az) * SPREAD_WORLD;
  // Behind and above, in the canopy: a distance trim so not every bird is at arm's reach.
  const trim = 0.55 + 0.45 * rnd();
  let t = now;
  for (let i = 0; i < notes; i++) {
    const slot = claimTone(g.chirpPool, t);
    if (!slot) break;
    const f0 = 2100 + rnd() * 2600;
    const dur = 0.04 + rnd() * 0.075;
    thump(slot, f0, f0 * (0.55 + rnd() * 0.85), level * trim * (0.6 + 0.6 * rnd()),
      0.008, dur, pan, t);
    t += dur + 0.03 + rnd() * 0.07;
  }
}

/**
 * What a surface sounds like, as numbers, without needing the game running.
 *
 * The whole "distinguishable with your eyes shut" requirement is unverifiable by
 * anyone on this project, so this is the next best thing: the same level, band and
 * grain arithmetic the live mix uses, evaluated for one surface at one operating
 * point, so the separation between surfaces can be printed and checked.
 */
export function surfaceProfile(
  soundKey: string, speedMs = 12, loadN = LOAD_REF, slipUsage = 0,
): Record<string, number | string> {
  const r = VOICES[soundKey] ?? FALLBACK_VOICE;
  const v = Math.max(0, speedMs);
  const load = clamp01(loadN / LOAD_REF);
  const speedTerm = 1 - Math.exp(-v / (SPEED_REF * 0.62));
  const slipT = clamp01((slipUsage - SLIP_GATE) / (1 - SLIP_GATE));
  return {
    surface: soundKey,
    level: +(r.level * speedTerm * (0.3 + 0.8 * load)).toFixed(4),
    bandHz: +(r.bandHz + r.bandPerMs * v).toFixed(1),
    bandQ: r.bandQ,
    brightDb: r.brightDb,
    grainPerSec: +(r.grainPerM * v * (0.4 + 0.7 * load)).toFixed(2),
    grainPitchHz: r.grainHz,
    grainQ: r.grainQ,
    grainLevel: r.grainLevel,
    slipLevel: +(r.slipLevel * Math.pow(slipT, 1.45) * speedTerm * (0.35 + 0.75 * load)).toFixed(4),
    slipBandHz: +(r.slipHz * (0.78 + 0.55 * slipT) + 9 * v).toFixed(1),
  };
}

// ---------------------------------------------------------------------------------
// Offline verification. The only way anyone here can produce evidence about the DSP.
// ---------------------------------------------------------------------------------

/**
 * A place, described the way the *world* describes one: stems, enclosure, ranges.
 *
 * Deliberately not a set of wet gains. The probe runs those through `wetLevelsFor` —
 * the same function the live update calls — so the offline render is driven by the real
 * model and cannot quietly diverge from it.
 */
export interface SpacePlace {
  name: string;
  /** Stems inside FOREST_PROBE_M, and the mean range of the nearest few, m. */
  stems: number;
  treeNearM: number;
  /** Landform enclosure 0..1, the wall's range in m, and its pan. */
  confinement: number;
  wallDistM: number;
  slapPan: number;
  rain: number;
}

/**
 * The places the model claims to tell apart. Every figure here is taken from what the
 * probes actually read in a capture run rather than invented: stem counts inside 26 m
 * on `descend` run 9..83 with a median of 46, mean nearest range 3.2..12.6 m, and
 * confinement 0.07..0.57 with the wall between 24 and 179 m.
 */
export const SPACE_PLACES: SpacePlace[] = [
  // Exposed summit: above the treeline, nothing above the horizon in any bearing.
  { name: 'summit', stems: 0, treeNearM: FOREST_PROBE_M, confinement: 0.05, wallDistM: 0, slapPan: 0, rain: 0 },
  // Closed timber: the median of what `descend` measures once it drops into the trees.
  { name: 'forest', stems: 46, treeNearM: 5.4, confinement: 0.34, wallDistM: 82, slapPan: 0.2, rain: 0 },
  // Open drainage: the trees thin out and the walls come up on both sides.
  { name: 'gully', stems: 5, treeNearM: 22, confinement: 0.62, wallDistM: 55, slapPan: -0.7, rain: 0 },
  // Slapback alone, hard left. Isolates the echo from the two symmetric tails so the
  // "it comes back from over there" claim is legible as an L-R number rather than being
  // buried under a diffuse field that is wide in both ears by construction.
  { name: 'slap-only', stems: 0, treeNearM: FOREST_PROBE_M, confinement: 0.62, wallDistM: 55, slapPan: -0.9, rain: 0 },
  // The two tails on their own. A place is a blend, so an RT60 measured on a blend is
  // the longer component's RT60 no matter how quiet it is — Schroeder integration is
  // level-blind by construction. These two rows are the only honest measurement of what
  // each impulse response actually is.
  { name: 'ir-forest', stems: 46, treeNearM: 5.4, confinement: 0.34, wallDistM: 82, slapPan: 0, rain: 0 },
  { name: 'ir-open', stems: 0, treeNearM: FOREST_PROBE_M, confinement: 0.62, wallDistM: 55, slapPan: 0, rain: 0 },
];

/** Schroeder backward integration: the -60 dB time implied by the -5..-25 dB slope,
 *  which is the standard way to get an RT60 out of a decay that never reaches -60. */
function rt60From(energy: Float32Array, sr: number): number {
  let total = 0;
  for (let i = 0; i < energy.length; i++) total += energy[i];
  if (total <= 1e-12) return 0;
  // Backward cumulative sum, in dB relative to the total.
  let acc = 0;
  let i5 = -1;
  let i25 = -1;
  const db = new Float32Array(energy.length);
  for (let i = energy.length - 1; i >= 0; i--) {
    acc += energy[i];
    db[i] = 10 * Math.log10(Math.max(acc / total, 1e-12));
  }
  for (let i = 0; i < db.length; i++) {
    if (i5 < 0 && db[i] <= -5) i5 = i;
    if (i25 < 0 && db[i] <= -25) { i25 = i; break; }
  }
  if (i5 < 0 || i25 <= i5) return 0;
  return ((i25 - i5) / sr) * 3; // -20 dB span scaled to -60
}

/**
 * Render the real space chain offline and measure it.
 *
 * `OfflineAudioContext` needs no user gesture and no audio device, so this runs in the
 * capture harness's browser — the same browser that never gestures and therefore never
 * has a live `AudioContext`. The chain built here is the same `SpaceChain` class, with
 * the same procedurally generated impulse responses, driven with the same parameter
 * values the live update would write. What it returns is measured off rendered samples:
 *
 *  - `rt60S`      Schroeder RT60 of the tail, s.
 *  - `firstMs`    when the first reflection arrives, ms — the enclosure cue.
 *  - `bright`     high-band over low-band energy, split at ~1.5 kHz. Forest dark, rock bright.
 *  - `corr`       L/R correlation. 1 = mono blob at the centre, 0 = a room around you.
 *  - `wetRms`     how much room there is at all.
 *  - `lrDb`       L minus R level, dB — proves the slapback comes from its bearing.
 */
export async function probeSpace(
  p: SpacePlace, seconds = 3, sr = 44100,
): Promise<Record<string, number | string>> {
  const OAC: typeof OfflineAudioContext | undefined =
    (globalThis as unknown as { OfflineAudioContext?: typeof OfflineAudioContext })
      .OfflineAudioContext;
  if (!OAC) return { name: p.name, available: 0 };
  // The live model, run for this place. Not a second copy of it.
  const closure = p.stems / (p.stems + FOREST_HALF_STEMS);
  wetLevelsFor(closure, p.confinement, p.treeNearM, p.wallDistM, p.rain);
  const set = { ...wetOut };

  const n = Math.floor(sr * seconds);
  const oc = new OAC(2, n, sr);
  const space = new SpaceChain(oc, oc.destination);

  space.gForest.gain.value = set.forest;
  space.gOpen.gain.value = set.open;
  for (let i = 0; i < space.erDelay.length; i++) {
    space.erDelay[i].delayTime.value = clamp(set.erBaseS * ER_MUL[i], 0.001, 0.49);
    space.erGain[i].gain.value = set.er * (1 - i * 0.17);
    space.erLp[i].frequency.value = 3800 - i * 620;
  }
  space.slapDelay.delayTime.value = set.slapS;
  space.slapGain.gain.value = set.slap;
  space.slapPan.pan.value = p.slapPan;
  space.slapLp.frequency.value = clamp(5200 * Math.exp(-p.wallDistM / 150) + 700, 300, 9000);
  // Isolating cases: everything except the one path under test is muted, so the number
  // that comes back is that path's and not the loudest neighbour's.
  const only = (forest: number, open: number, er: number, slap: number) => {
    space.gForest.gain.value = forest;
    space.gOpen.gain.value = open;
    for (const g of space.erGain) g.gain.value = er;
    space.slapGain.gain.value = slap;
  };
  if (p.name === 'slap-only') only(0, 0, 0, set.slap);
  else if (p.name === 'ir-forest') only(set.forest, 0, 0, 0);
  else if (p.name === 'ir-open') only(0, set.open, 0, 0);
  space.out.gain.value = WET_MASTER;

  // A 4 ms noise burst: broadband enough to excite the whole tail, short enough that the
  // measured decay is the room's and not the source's.
  const burstLen = Math.floor(sr * 0.004);
  const burst = oc.createBuffer(1, burstLen, sr);
  const bd = burst.getChannelData(0);
  const brnd = mulberry32(0x51ed270b);
  for (let i = 0; i < burstLen; i++) bd[i] = (brnd() * 2 - 1) * (1 - i / burstLen);
  const src = oc.createBufferSource();
  src.buffer = burst;
  src.connect(space.input);
  src.start(0);

  const rendered = await oc.startRendering();
  const L = rendered.getChannelData(0);
  const R = rendered.getChannelData(1);

  // Envelope, and the measures taken off it.
  const env = new Float32Array(L.length);
  let peak = 0;
  let sumL = 0;
  let sumR = 0;
  let sumLR = 0;
  for (let i = 0; i < L.length; i++) {
    const e = L[i] * L[i] + R[i] * R[i];
    env[i] = e;
    if (e > peak) peak = e;
    sumL += L[i] * L[i];
    sumR += R[i] * R[i];
    sumLR += L[i] * R[i];
  }
  let first = -1;
  const thresh = Math.max(peak * 0.02, 1e-9);
  for (let i = 0; i < env.length; i++) if (env[i] > thresh) { first = i; break; }

  // Brightness: one-pole split at 1.5 kHz on the mono sum.
  const k = 1 - Math.exp((-2 * Math.PI * 1500) / sr);
  let lp = 0;
  let lo = 0;
  let hi = 0;
  for (let i = 0; i < L.length; i++) {
    const x = (L[i] + R[i]) * 0.5;
    lp += (x - lp) * k;
    lo += lp * lp;
    hi += (x - lp) * (x - lp);
  }

  const rms = Math.sqrt((sumL + sumR) / (2 * L.length));
  const corr = sumL > 1e-12 && sumR > 1e-12 ? sumLR / Math.sqrt(sumL * sumR) : 0;
  return {
    name: p.name,
    available: 1,
    // Echoed so the row reads as "this world produced this room".
    closure: +closure.toFixed(3),
    wetForest: +set.forest.toFixed(3),
    wetOpen: +set.open.toFixed(3),
    wetER: +set.er.toFixed(3),
    wetSlap: +set.slap.toFixed(3),
    rt60S: +rt60From(env, sr).toFixed(3),
    firstMs: first < 0 ? -1 : +((first / sr) * 1000).toFixed(1),
    bright: +(hi / Math.max(lo, 1e-12)).toFixed(3),
    corr: +corr.toFixed(3),
    wetRms: +rms.toFixed(6),
    lrDb: +(10 * Math.log10(Math.max(sumL, 1e-12) / Math.max(sumR, 1e-12))).toFixed(2),
  };
}

/** Every place, rendered and measured. One call, one table. */
export async function probeAllSpaces(): Promise<Record<string, number | string>[]> {
  const out: Record<string, number | string>[] = [];
  for (const s of SPACE_PLACES) out.push(await probeSpace(s));
  return out;
}

/**
 * The placement arithmetic, exposed. Given a world offset from the listener and the
 * listener's heading, what pan / bearing / back-of-head weight does a source get?
 *
 * This is how "the creek has a bearing" and "roost is behind you" are checked without
 * ears: put a source 30 m to the rider's left and the pan must be about -0.95 with a
 * bearing near -90; put it at the rear contact patch and `back` must be ~1.
 */
export function probePlacement(
  dx: number, dz: number, headingRad = 0, spread = SPREAD_WORLD,
): Record<string, number> {
  const saved = listenYaw;
  listenYaw = headingRad;
  placeWorld(dx, dz, spread);
  const r = {
    pan: +place.pan.toFixed(4),
    bearingDeg: +placeBearingDeg().toFixed(2),
    back: +place.back.toFixed(4),
    tiltDb: +place.tiltDb.toFixed(2),
    trim: +place.trim.toFixed(4),
    distM: +place.distM.toFixed(3),
  };
  listenYaw = saved;
  return r;
}

// ---------------------------------------------------------------------------------
// The whole mix, rendered offline to a file. The point of round 3.
// ---------------------------------------------------------------------------------

/**
 * What a render is asked for.
 *
 * `rows` default to whatever is in the recording buffer, so the normal shape is: drive a
 * scenario with `record()` on, then call `renderMix()` and write the bytes.
 */
export interface RenderMixOptions {
  rows?: number[][];
  /** Frame index range, half-open. Defaults to the whole recording. */
  from?: number;
  to?: number;
  sampleRate?: number;
  /** Extra seconds rendered past the last frame so tails and reverb are not truncated. */
  tailS?: number;
  /**
   * Mute every bus except one, to measure what a voice actually contributes to the mix
   * rather than what its nominal gain says. `'dry'` keeps every bus and mutes the room.
   */
  solo?: 'tire' | 'mech' | 'impact' | 'wind' | 'amb' | 'dry' | null;
  /** Deterministic one-shot sequence. Same seed, same file, byte for byte. */
  seed?: number;
}

export interface RenderMixResult {
  /** RIFF/WAVE, 16-bit PCM stereo, base64. Write it to a file and press play. */
  wavBase64: string;
  seconds: number;
  sampleRate: number;
  frames: number;
  /** Measured off the rendered samples, not off the model. */
  peak: number;
  rms: number;
  rmsDb: number;
  /** Samples at or past full scale. Non-zero means the master is clipping. */
  clipped: number;
  /** Per-channel RMS and the correlation between them: 1.0 is a mono file. */
  rmsL: number;
  rmsR: number;
  corr: number;
  /** Octave-band energy of the rendered mix, dB relative to the loudest band. Reading
   *  this is how someone who cannot hear checks that a clip is not all rumble or all
   *  hiss before handing it to someone who can. */
  bandsDb: number[];
  bandHz: number[];
  /**
   * Amplitude-modulation depth at the update rate and its neighbours, dB relative to the
   * envelope's own RMS.
   *
   * The classic failure of a mix whose every parameter is rewritten once per frame is a
   * buzz at the frame rate — a discontinuity, a retriggered one-shot or a stepped gain
   * landing on the same 16.7 ms grid until it becomes a pitch. It is inaudible in any
   * still picture and obvious the moment anyone plays the file, so it is exactly the kind
   * of thing this project needs measured rather than looked at. Anything under about
   * -20 dB here is noise-floor; a real buzz shows up at -6 dB or better.
   */
  modDb: { hz: number; db: number }[];
}

/** Octave crossovers, Hz. Ten bands: below 40, eight octaves, and above 10240. */
const BAND_EDGES = [40, 80, 160, 320, 640, 1280, 2560, 5120, 10240];

/**
 * Render recorded frames through a real `AudioGraph` in an `OfflineAudioContext`.
 *
 * This is the answer to the standing complaint that nobody on this project has produced
 * a second of audible output. Everything it uses is the shipping code: the same
 * `AudioGraph` constructor, the same generated noise buffers and impulse responses, the
 * same `renderFrame()` the live update calls, driven by frames the model computed while
 * the capture harness rode a real scenario. Nothing here is a simulation of the mix.
 *
 * Two things had to be true for it to work and both are now:
 *  1. `AudioGraph` takes a `BaseAudioContext` (`SpaceChain` already did — that is where
 *     the idea came from and it is why the reverb was the only thing ever measured).
 *  2. The per-frame parameter writes are a function of a recorded frame rather than of
 *     live locals, so they can be replayed at a clock that is not the wall clock.
 */
export async function renderMix(opts: RenderMixOptions = {}): Promise<RenderMixResult | null> {
  const OAC: typeof OfflineAudioContext | undefined =
    (globalThis as unknown as { OfflineAudioContext?: typeof OfflineAudioContext })
      .OfflineAudioContext;
  if (!OAC) return null;

  const rows = opts.rows ?? recRows;
  const from = Math.max(0, opts.from ?? 0);
  const to = Math.min(rows.length, opts.to ?? rows.length);
  if (to - from < 2) return null;
  const sr = opts.sampleRate ?? 48000;
  const tail = opts.tailS ?? 1.6;

  const dtIdx = FRAME_KEYS.indexOf('dt');
  let span = 0;
  for (let i = from; i < to; i++) span += rows[i][dtIdx] || 1 / 60;

  const n = Math.max(1024, Math.ceil((span + tail) * sr));
  const oc = new OAC(2, n, sr);
  const g = new AudioGraph(oc);
  const st = newRenderState(opts.seed ?? 0x51ed270b);

  // Solo by muting buses at the point they meet the master, which mutes their reverb
  // send with them — so a solo is genuinely "what does this bus put into the mix",
  // room included, and the numbers add up against the full render.
  const solo = opts.solo ?? null;
  if (solo) {
    const keep: Record<string, GainNode[]> = {
      tire: [g.busTire],
      mech: [g.busMech],
      impact: [g.busImpact],
      wind: [g.busWind],
      amb: [g.busAmb, g.busAmbFar],
      dry: [g.busTire, g.busMech, g.busImpact, g.busWind, g.busAmb, g.busAmbFar],
    };
    const all = [g.busTire, g.busMech, g.busImpact, g.busWind, g.busAmb, g.busAmbFar];
    const kept = keep[solo] ?? all;
    for (const b of all) if (!kept.includes(b)) b.gain.value = 0;
    if (solo === 'dry') g.space.out.gain.value = 0;
  }

  const f = blankFrame();
  let t = 0;
  for (let i = from; i < to; i++) {
    rowToFrame(rows[i], f);
    if (!(f.dt > 0) || f.dt > 0.5) f.dt = 1 / 60;
    renderFrame(g, f, t, st);
    if (solo === 'dry') setParam(g.space.out.gain, 0, t, TAU_LEVEL);
    t += f.dt;
  }

  const buf = await oc.startRendering();
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);

  let peak = 0;
  let sumL = 0;
  let sumR = 0;
  let sumLR = 0;
  let clipped = 0;
  for (let i = 0; i < L.length; i++) {
    const a = Math.abs(L[i]);
    const b = Math.abs(R[i]);
    if (a > peak) peak = a;
    if (b > peak) peak = b;
    if (a >= 0.999 || b >= 0.999) clipped++;
    sumL += L[i] * L[i];
    sumR += R[i] * R[i];
    sumLR += L[i] * R[i];
  }
  const rmsL = Math.sqrt(sumL / L.length);
  const rmsR = Math.sqrt(sumR / R.length);
  const rms = Math.sqrt((sumL + sumR) / (2 * L.length));

  // Octave-band energy: one one-pole lowpass per crossover, all fed the same mono sum,
  // each band being the difference between neighbouring lowpass outputs. Crude, and it
  // does not need to be anything else — the question it answers is "is this clip all
  // rumble, all hiss, or shaped like a bicycle on dirt", and that is a 3 dB question.
  const nb = BAND_EDGES.length + 1;
  const bands = new Float64Array(nb);
  // Two cascaded one-poles per crossover — 12 dB/octave. One pole was not enough: with a
  // 6 dB/octave skirt the "below 40 Hz" band collects most of the next two octaves too,
  // and the first pass of this measurement therefore reported the sub-sonic band as the
  // loudest in the file even after a highpass had removed it. A measurement that leaks
  // three octaves is worse than no measurement, because it gets acted on.
  const lpA = new Float64Array(BAND_EDGES.length);
  const lpB = new Float64Array(BAND_EDGES.length);
  const k = BAND_EDGES.map((hz) => 1 - Math.exp((-2 * Math.PI * hz) / sr));
  for (let i = 0; i < L.length; i++) {
    const x = (L[i] + R[i]) * 0.5;
    for (let bnd = 0; bnd < BAND_EDGES.length; bnd++) {
      lpA[bnd] += (x - lpA[bnd]) * k[bnd];
      lpB[bnd] += (lpA[bnd] - lpB[bnd]) * k[bnd];
    }
    bands[0] += lpB[0] * lpB[0];
    for (let bnd = 1; bnd < BAND_EDGES.length; bnd++) {
      const d = lpB[bnd] - lpB[bnd - 1];
      bands[bnd] += d * d;
    }
    const top = x - lpB[BAND_EDGES.length - 1];
    bands[nb - 1] += top * top;
  }
  let bandMax = 0;
  for (const b of bands) if (b > bandMax) bandMax = b;
  const bandsDb = Array.from(bands, (b) =>
    +(10 * Math.log10(Math.max(b, 1e-20) / Math.max(bandMax, 1e-20))).toFixed(1));

  return {
    modDb: modulationDepth(L, R, sr),
    wavBase64: encodeWavBase64(L, R, sr),
    seconds: +(L.length / sr).toFixed(3),
    sampleRate: sr,
    frames: to - from,
    peak: +peak.toFixed(5),
    rms: +rms.toFixed(6),
    rmsDb: +(20 * Math.log10(Math.max(rms, 1e-9))).toFixed(2),
    clipped,
    rmsL: +rmsL.toFixed(6),
    rmsR: +rmsR.toFixed(6),
    corr: +(sumL > 1e-12 && sumR > 1e-12 ? sumLR / Math.sqrt(sumL * sumR) : 0).toFixed(3),
    bandsDb,
    bandHz: BAND_EDGES,
  };
}

/** Modulation frequencies interrogated, Hz: half the update rate, the update rate, and
 *  twice it. A per-frame discontinuity puts energy at all three. */
const MOD_HZ = [30, 60, 120];

/**
 * How strongly the mix is amplitude-modulated at the update rate.
 *
 * Rectify, follow with a one-pole at 400 Hz, decimate, remove DC, then a Goertzel at each
 * frequency of interest. Ten lines, no FFT, and it answers the one question a picture of
 * the spectrum cannot: whether the thing that looks like vertical striping is a real
 * 60 Hz buzz riding on the whole mix or an artefact of the way the picture was drawn.
 */
function modulationDepth(L: Float32Array, R: Float32Array, sr: number): { hz: number; db: number }[] {
  const dec = Math.max(1, Math.floor(sr / 2400));
  const decSr = sr / dec;
  const n = Math.floor(L.length / dec);
  if (n < 64) return MOD_HZ.map((hz) => ({ hz, db: -99 }));
  const env = new Float64Array(n);
  const k = 1 - Math.exp((-2 * Math.PI * 400) / sr);
  let e = 0;
  let mean = 0;
  for (let i = 0, j = 0; i < L.length; i++) {
    const x = Math.abs(L[i]) + Math.abs(R[i]);
    e += (x - e) * k;
    if (i % dec === 0 && j < n) { env[j] = e; mean += e; j++; }
  }
  mean /= n;
  let rms = 0;
  for (let i = 0; i < n; i++) { env[i] -= mean; rms += env[i] * env[i]; }
  rms = Math.sqrt(rms / n);
  return MOD_HZ.map((hz) => {
    const w = (2 * Math.PI * hz) / decSr;
    const coeff = 2 * Math.cos(w);
    let s1 = 0;
    let s2 = 0;
    for (let i = 0; i < n; i++) {
      const s0 = env[i] + coeff * s1 - s2;
      s2 = s1;
      s1 = s0;
    }
    // Magnitude of the bin, scaled to the amplitude of a sinusoid of that frequency.
    const mag = (2 * Math.sqrt(Math.max(0, s1 * s1 + s2 * s2 - coeff * s1 * s2))) / n;
    return { hz, db: +(20 * Math.log10(Math.max(mag / Math.SQRT2, 1e-9) / Math.max(rms, 1e-9))).toFixed(1) };
  });
}

/**
 * 16-bit PCM RIFF/WAVE, base64.
 *
 * Written by hand because the alternative is a dependency, and this project ships no
 * binary assets and as few dependencies as it can get away with. Base64 rather than a
 * transferable because the consumer is `page.evaluate()` in a probe script, which
 * marshals JSON.
 */
function encodeWavBase64(L: Float32Array, R: Float32Array, sr: number): string {
  const n = L.length;
  const bytes = new Uint8Array(44 + n * 4);
  const dv = new DataView(bytes.buffer);
  const ascii = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) bytes[off + i] = s.charCodeAt(i);
  };
  ascii(0, 'RIFF');
  dv.setUint32(4, 36 + n * 4, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  dv.setUint32(16, 16, true);
  dv.setUint16(20, 1, true); // PCM
  dv.setUint16(22, 2, true); // stereo
  dv.setUint32(24, sr, true);
  dv.setUint32(28, sr * 4, true);
  dv.setUint16(32, 4, true);
  dv.setUint16(34, 16, true);
  ascii(36, 'data');
  dv.setUint32(40, n * 4, true);
  let o = 44;
  for (let i = 0; i < n; i++) {
    const l = Math.max(-1, Math.min(1, L[i]));
    const r = Math.max(-1, Math.min(1, R[i]));
    dv.setInt16(o, l < 0 ? l * 0x8000 : l * 0x7fff, true);
    dv.setInt16(o + 2, r < 0 ? r * 0x8000 : r * 0x7fff, true);
    o += 4;
  }
  // Chunked so a multi-megabyte file does not blow the argument limit of apply().
  let s = '';
  const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) {
    s += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CH)));
  }
  return btoa(s);
}

export { mix as audioMix };
